// Chinese (Simplified) — complete translations
// Keys mirror es.ts structure exactly

export const zh = {
  // General
  appName: 'V·IANet Governance',
  brandName: 'VALOR·IA Governance',
  poweredBy: 'powered by',

  // Sidebar
  sidebar: {
    explorer: '文档浏览器',
    processMap: '流程地图',
    collapseAll: '▶ 全部收起',
    expandAll: '▼ 全部展开',
    strategic: '战略流程',
    operational: '运营流程',
    support: '支持流程',
    structure: '组织架构',
    process: '流程',
    information: '信息',
    compliance: '合规',
    tech: '技术',
    reputation: '声誉',
    settings: '设置',
    ingenierias: '工程学',
    actionCenter: '操作中心',
    allOrgs: '所有组织',
    cambiarTenant: '切换组织',
    salir: '退出',
    userMenu: {
      miCuenta: '我的账户',
      backoffice: '后台管理',
      cambiarOrganizacion: '切换组织',
      cerrarSesion: '退出登录',
      superadminBadge: '平台管理员',
      rolBadge: '角色',
    },
    pageTitle: {
      processMap: '公司治理',
      structure: '组织架构',
      process: '流程体系',
      information: '商业信息管理',
      compliance: '合规管理',
      tech: '信息技术',
      reputation: '声誉传播管理',
      settings: '设置',
    },
  },

  // Explorer
  explorer: {
    title: '文档浏览器',
    subtitle: (total: number) => `管理系统中共 ${total} 个文档`,
    totalDocs: '文档总数',
    published: '已发布',
    draft: '草稿',
    inReview: '审核中',
    allTypes: '所有类型',
    allStates: '所有状态',
    allProcesses: '所有流程',
    search: '搜索文档...',
    clearFilters: '✕ 清除',
    showing: (from: number, to: number, total: number) =>
      `显示第 ${from}–${to} 条，共 ${total} 个文档`,
    noResults: '未找到文档',
    newDocument: '+ 新建文档',
    previous: '← 上一页',
    next: '下一页 →',
  },

  // Table headers
  table: {
    code: '编号',
    title: '标题',
    type: '类型',
    process: '流程',
    status: '状态',
    direction: '方向',
    edition: '版本',
    date: '日期',
  },

  // Document types
  docType: {
    PR: '程序文件',
    NC: '企业标准',
    IT: '作业指导书',
    MN: '手册',
    PA: '行动计划',
    RE: '报告',
    FC: '流程特性表',
    FL: '流程图',
    HE: '工具',
    MO: '模板',
    PT: '协议',
    FR: '职能角色卡',
    FP: '岗位卡',
    FG: '治理角色卡',
  },

  // Document states
  status: {
    BORRADOR: '草稿',
    EN_REVISION: '审核中',
    APROBADO: '已批准',
    PUBLICADO: '已发布',
    ARCHIVADO: '已归档',
  },

  // Version states
  versionStatus: {
    BORRADOR: '草稿',
    EN_REVISION: '审核中',
    ENVIADO: '已提交',
    APROBADO: '已批准',
    PUBLICADO: '生效中',
    ARCHIVADO: '已归档',
    RECHAZADO: '已拒绝',
  },

  // Document types (match TipoDocumental enum)
  docTypes: {
    PR: '程序文件',
    NC: '企业标准',
    IT: '作业指导书',
    MN: '手册',
    PA: '行动计划',
    RE: '报告',
    FC: '流程特性表',
    FL: '流程图',
    HE: '工具',
    MO: '模板',
    PT: '协议',
    FR: '职能角色卡',
    FP: '岗位卡',
    FG: '治理角色卡',
  },

  // Process typologies
  typology: {
    PE: '战略流程',
    PO: '运营流程',
    PS: '支持流程',
  },

  // Viewer
  viewer: {
    documentContent: '文档内容',
    searchContent: '搜索内容...',
    cover: '封面',
    tabs: {
      content: '内容',
      metadata: '元数据',
      versions: '版本',
      references: '引用',
    },
    company: '公司',
    direction: '方向',
    edition: '版本',
    date: '日期',
    process: '流程',
    elaboratedBy: '编制人',
    reviewedBy: '审核人',
    approvedBy: '审批人',
    approvalDate: '审批日期',
    exportPDF: '导出PDF',
    edit: '编辑',
    lifecycle: '生命周期',
    outgoing: '引用到',
    incoming: '被引用',
    noReferences: '无交叉引用',
    versionHistory: '版本历史',
    noVersions: '无版本',
    changeDescription: '变更描述',
    borradorEnEdicion: '有草稿正在编辑中',
    verBorrador: '查看草稿',
    eliminarBorrador: '删除草稿',
    confirmarEliminar: '删除草稿？',
    eliminarBorradorDesc: '此操作将永久删除草稿。已发布的版本不受影响。',
    borradorEliminado: '草稿已删除',
    eliminar: '删除',
    archivar: '归档',
    confirmarArchivar: '归档文档？',
    archivarDesc: '文档将被归档并不再处于有效状态。此操作可以撤销。',
    documentoArchivado: '文档已归档',
  },

  // Editor
  editor: {
    save: '保存',
    close: '关闭',
    saving: '保存中...',
    saved: '已保存',
    savedAt: (time: string) => `已保存：${time}`,
    autosave: '每30秒自动保存',
    unsavedChanges: '未保存的更改',
    addBlock: '+ 添加块',
    aiAssistant: '✨ AI助手',
    wordCount: (n: number) => `${n} 字`,
    // GenerateAIDialog / ImportDocxDialog keys
    generateAI: '使用VALOR·IA生成',
    generateAIDesc: '描述您想为本文档生成的内容。',
    generateUnavailable: 'AI生成暂不可用，将在下一版本提供。',
    generatePlaceholder: '为此程序生成定义部分...',
    generate: '生成',
    insertAtEnd: '插入到末尾',
    insertAtCursor: '插入到光标处',
    importDocx: '导入DOCX',
    dragDocx: '拖拽.docx文件或点击选择',
    processingDocx: '处理中...',
    insertContent: '插入内容',
    crossRefPlaceholder: '按代码搜索（如：PE.21.NC）...',
    crossRefNoResults: '无结果。按Enter手动插入。',
    crossRefNavigate: '导航',
    crossRefSelect: '选择',
    crossRefClose: '关闭',
    styles: {
      headings: '标题',
      paragraph: '段落',
      lists: '列表',
      table: '表格',
      character: '字符',
    },
    toolbar: {
      undo: '撤销',
      redo: '重做',
      bold: '粗体',
      italic: '斜体',
      underline: '下划线',
      bulletList: '无序列表',
      orderedList: '有序列表',
      table: '表格',
      crossRef: '交叉引用',
      image: '图片',
      normNote: '规范注释',
      codeBlock: '代码块',
      hr: '水平线',
      importDocx: '导入DOCX',
      generateAI: '使用AI生成',
    },
    table: {
      rowAbove: '上方插入行',
      rowBelow: '下方插入行',
      colLeft: '左侧插入列',
      colRight: '右侧插入列',
      deleteRow: '删除行',
      deleteCol: '删除列',
      deleteTable: '删除表格',
      mergeCells: '合并单元格',
      splitCell: '拆分单元格',
    },
    floatingToolbar: {
      improveWriting: '改善写作',
      expandContent: '扩展内容',
      summarize: '摘要',
      translate: '翻译 ES↔EN',
      verifyCoherence: '验证一致性',
      suggestRefs: '建议引用',
    },
    aiPanel: {
      title: 'AI助手',
      subtitle: '文档治理',
      draftSection: '起草章节',
      completeStructure: '完善结构',
      verifyCoherence: '验证一致性',
      crossRefs: '交叉引用',
      translate: '翻译',
      placeholder: '输入您的问题...',
    },
    suggestion: {
      accept: '接受 (Tab)',
      discard: '放弃',
      regenerate: '重新生成',
      badge: '✨ AI建议',
    },
  },

  // Process sheet
  processSheet: {
    exportPDF: '导出PDF',
    edit: '编辑',
    aiAssistant: '✨ AI助手',
    sections: {
      identification: '标识',
      description: '描述',
      inputsOutputs: '输入和输出',
      reportsRequirements: '报告和要求',
      documentation: '文档',
      tools: '工具',
      indicators: '指标',
    },
    fields: {
      name: '名称',
      code: '编号',
      type: '类型',
      group: '功能组',
      responsible: '负责人',
      involved: '参与者',
      scope: '范围',
      inputs: '输入',
      outputs: '输出',
      origin: '来源',
      destination: '目标',
      reports: '报告',
      requirements: '要求',
      objective: '目标',
      frequency: '频率',
      phase: '阶段',
    },
    freq: {
      mensual: '每月',
      trimestral: '每季度',
      semestral: '每半年',
      anual: '每年',
    },
    aiActions: {
      completeDescription: '完善描述',
      suggestIO: '建议输入/输出',
      proposeIndicators: '提议指标',
      verifyCoherence: '验证一致性',
      generateSheet: '生成完整表',
    },
    editing: '编辑中...',
    addItem: '添加',
    removeItem: '删除',
    saved: '已保存',
    noData: '无数据记录',
    placeholders: {
      descripcion: '描述...',
      unidad: '单位...',
      objetivo: '目标...',
      frecuencia: '频率...',
      destinatario: '收件人...',
      codigo: '代码...',
      modulo: 'V·IAnet模块...',
    },
  },

  // Workflow actions (ampliado en 0628 para WorkflowBar shared ADR-025)
  workflow: {
    sendToReview: '提交审核',
    sendToApproval: '提交审批',
    approve: '批准',
    reject: '驳回',
    publish: '发布',
    returnToDraft: '退回草稿',
    newEdition: '新版本',
    archive: '归档',
    discard: '丢弃',
    reopen: '重新打开',
    motivoRechazoPrompt: '驳回原因（必填）',
    motivoRechazoRequerido: '原因不能为空。',
    confirmarAccion: '确认此操作？',
    // 0665：送审动作的跨工件标准字段。
    asuntoLabel: '主题',
    asuntoPlaceholder: '变更的简短摘要（例如："更新审批截止日期"）',
    asuntoHint: '至少 5 个字符 · 简短清晰的摘要',
    asuntoMinError: '主题至少需要 5 个字符。',
    alcanceLabel: '范围',
    alcancePlaceholder: '描述变更内容、原因以及对组织的影响（涉及哪些流程、领域或人员）。',
    alcanceHint: '至少 20 个字符 · 描述什么/为什么/如何',
    alcanceMinError: '范围至少需要 20 个字符。',
    // 0651: 工作流动作的悬停提示（跨工件 · 共享 WorkflowBar）。
    hints: {
      enviarRevision: '将版本发送给审批人。审批人会收到通知，版本将等待其决定。',
      aprobar: '确认该版本并将其状态标记为已批准，准备发布。如已配置则生成 SES 签名。',
      rechazar: '将版本退回为已驳回状态并附原因。所有者可重新打开进行修正。',
      publicar: '替换当前已发布的版本。先前已发布的版本将进入逻辑归档。',
      descartar: '永久放弃草稿。该操作不可撤销 — 版本将保持已丢弃状态。',
      archivar: '从活动列表中移除已发布版本。版本将保持已归档状态。',
      reabrir: '将驳回的版本恢复为草稿状态，以便所有者编辑并重新提交。',
    },
    approvalPreview: {
      willBeSentTo: '将发送给:',
      sourceHierarchy: '层级',
      sourceProcess: '流程',
      sourceFallback: '系统管理',
    },
    // 0661 ADR-025 ext: ArtefactoWorkflowBar 共享组件跨工件键
    // (组织结构图 + 文档 + 流程图 · 统一 UI)
    bar: {
      loading: '加载工作流…',
      firstEditionDescription: '第一版',
      createFirstDraft: '创建草稿',
      viewHistory: '查看版本历史',
      historyButton: (n: number) => `历史 (${n})`,
      newEditionDescription: (n: number) => `第 ${n} 版`,
      newEditionButton: (n: number) => `新版本 (第 ${n} 版)`,
      historyVersions: '版本历史',
      close: '关闭',
      noVersionsYet: '暂无版本。',
      onScreen: '当前显示',
      publishedAt: '发布于',
      editionTooltip: (n: number) => `第 ${n} 版`,
      viewEditionTooltip: (n: number) => `查看第 ${n} 版`,
      stageBadge: (pos: number, total: number, accion: string) =>
        `阶段 ${pos}/${total} · ${accion}`,
      stageTooltip: (pos: number, total: number, accion: string) =>
        `处于第 ${pos} 阶段，共 ${total} 阶段：${accion}`,
      errorCreatingDraft: (msg: string) => `创建草稿出错：${msg}`,
      // 0670 — 最新版本被拒/丢弃时回退到当前发布版 + 只读可见性
      fallbackVigenteBadge: '最新版本已被拒绝',
      fallbackVigenteTooltip: '最新版本已被拒绝或丢弃 · 正在显示当前发布的版本。',
      soloVigenteBadge: '只读',
      soloVigenteTooltip: '您不参与此审批流程 · 只能查看已发布的版本。',
      estados: {
        BORRADOR: '草稿',
        EN_REVISION: '审核中',
        APROBADO: '已批准',
        PUBLICADO: '已发布',
        RECHAZADO: '已拒绝',
        DESCARTADO: '已废弃',
        ARCHIVADO: '已归档',
      },
    },
  },

  // Modal new document
  modal: {
    newDocument: '新建文档',
    selectProcess: '选择流程',
    documentType: '文档类型',
    documentTitle: '文档标题',
    direction: '方向',
    edition: '版本',
    autoCode: '自动生成编号',
    create: '创建文档',
    cancel: '取消',
  },

  // Quick search
  quickSearch: {
    placeholder: '搜索文档、流程...',
    recent: '最近',
    noResults: '无结果',
    hint: '⌘K 搜索',
  },

  // Common
  loading: '加载中...',
  error: '错误',
  notFound: '未找到',
  goBack: '返回',

  // Engineering names
  engineering: {
    organizacional: '组织',
    procesos: '流程',
    informacion: '信息',
    cumplimiento: '合规',
    tecnologica: '技术',
    reputacional: '声誉',
  },

  // V·IA Panel & Chat
  via: {
    titulo: 'V·IA — 治理助手',
    panel: {
      generar: '生成文档',
      auditar: '审计文档',
      traducir: '翻译',
      mejorar: '改善写作',
      ampliar: '扩展内容',
      resumir: '摘要',
      verificarTerminos: '验证术语',
      reformular: '重新表述',
      definirTermino: '定义术语',
    },
    chat: {
      placeholder: '输入您的查询...',
      enviar: '发送',
      saludo: '你好，我是 V·IA',
      descripcion: '您的企业治理助手。我了解系统中的所有流程、文档、人员和组织架构。',
      hint: '咨询流程、文档或法规相关问题。',
      procesando: '处理中...',
      asistenteListo: 'VIA助手已就绪',
      seleccioneAccion: '选择快速操作或输入消息。',
      errorConexion: '与VIA服务器连接错误。',
      errorRed: '网络错误',
      errorServidor: '服务器错误',
      accionCompletada: '操作已完成。',
      noDisponible: 'VIA助手尚不可用。',
      expandir: '展开聊天',
      contraer: '收起聊天',
    },
    contexto: '上下文',
    nuevoContexto: '新上下文',
    documentoCompleto: '完整文档',
    accionesRapidas: '快速操作',
    sinAcciones: '当前上下文和角色无可用操作。',
    abrirPanel: '打开VIA面板',
    cerrarPanel: '关闭面板',
    inteligenciaNormativa: '规范人工智能',
    copilot: {
      titulo: 'V·IA Copilot',
      completar: '补全此章节',
      corregir: '审查并纠正',
      sugerir: '建议改进',
      verificar: '验证合规性',
      aplicar: '应用',
      descartar: '放弃',
      copiar: '复制',
    },
  },

  // Sugerencias
  sugerencias: {
    titulo: '建议',
    pendientes: '{{count}} 条待处理建议',
    pendientes_plural: '{{count}} 条待处理建议',
    aceptar: '接受',
    rechazar: '拒绝',
    aceptarTodas: '全部接受',
    rechazarTodas: '全部拒绝',
    siguiente: '下一个',
    aceptarSugerencia: '接受建议',
    rechazarSugerencia: '拒绝建议',
  },

  // Topbar (通知铃, 下拉面板)
  topbar: {
    notifications: {
      title: '通知',
      empty: '没有通知',
      markAllRead: '全部标为已读',
      sectionPendientes: '待处理事项',
      sectionNotificaciones: '通知',
      tooltipBell: '通知',
      tagOrganigrama: '组织结构图',
      tagDocumento: '文档',
      tagFlujograma: '流程图',
      tagProceso: '过程',
    },
  },

  // Admin
  admin: {
    titulo: '管理',
    dashboardTitle: '管理面板',
    tooltips: {
      dashboard: {
        titulo: '管理面板',
        ayuda:
          'V·IAnet Governance 平台的控制中心。\n\n在这里您可以配置公司、管理用户和角色、激活模块并监控人工智能使用情况。\n\n此部分的更改会影响所有租户用户。',
      },
    },
    tabs: {
      dashboard: '控制面板',
      backoffice: '后台管理',
      empresa: '企业',
      usuarios: '用户',
      modulos: '模块',
      plataforma: '平台',
      ia: '人工智能',
      notificaciones: '通知',
      sign: '签名',
      workflows: '审批流程',
      i18n: '语言',
    },
    dashboard: {
      totalUsuarios: '活跃用户',
      totalDocumentos: '文档',
      totalProcesos: '流程',
      publicados: '已发布',
      modulos: '模块',
      totalFlujogramas: '流程图',
      totalAuditorias: '审计',
      actividadReciente: '最近活动',
      sinActividad: '无最近活动',
      saludSistema: '系统健康',
      baseDatos: '数据库',
      operativo: '正常运行',
      degradado: '性能下降',
      caido: '已停机',
      desconocido: '未知',
      planYUso: '套餐和使用情况',
      modulosActivos: '13个活跃模块',
      tokensIA: 'AI令牌',
      auditoria: '审计日志',
      filtrarUsuario: '过滤用户...',
      exportarCSV: 'CSV',
      fecha: '日期',
      usuario: '用户',
      accion: '操作',
      entidad: '实体',
      reindexar: '重建Meilisearch索引',
    },
    empresa: {
      pills: {
        brand: '品牌标识',
        regional: '区域设置',
        templates: '模板',
        governance: '治理',
        auth: '认证',
      },
      formatoFecha: '日期格式',
      proximamente: '功能开发中，即将推出。',
    },
    plataforma: {
      pills: {
        modules: '模块与计划',
        apps: 'V·IAnet 应用',
        plan: '套餐与限制',
      },
      appsTitle: 'V·IAnet 应用 (M12)',
      appsDesc: 'M12 V·IAnet 应用的子应用。仅在 M12 已激活时可用。',
      planCardTitle: '租户套餐与限制',
      planLabel: '授权套餐',
      maxUsuariosLabel: '最大用户数',
      dominioLabel: '自定义域名',
      dominioHint: '留空 = 使用默认域名。需要 DNS CNAME 指向 app.valoriagovernance.com。',
      planSaveBtn: '保存更改',
      planSaving: '保存中...',
      planSaveOk: '套餐和限制已成功更新',
      planSaveErr: '保存套餐时出错',
      appUpdateErr: '更新应用时出错',
    },
    ia: {
      pills: {
        status: '状态与消耗',
        config: '配置',
        chat: 'VALORIA 聊天',
        assist: 'VALORIA 助手',
        providers: '供应商',
        instructions: '指令',
        evals: '评估',
      },
      sinDatos: '暂无消耗数据',
      proximamente: '功能开发中，即将推出。',
      systemPrompt: '自定义系统提示',
      systemPromptPlaceholder: '租户AI助手的自定义指令...',
      proveedores: {
        titulo: '提供商和模型',
        ayuda:
          '系统有哪些可用的AI提供商？\n\n在这里配置每个提供商：API密钥、连接地址和默认模型。大多数是云服务（Mistral、Groq、NVIDIA等），但Ollama比较特殊——它在您自己的电脑上运行，数据不会发送到外部。\n\n激活提供商前，请点击"测试"确认连接正常。',
      },
      chat: {
        titulo: '聊天配置',
        ayuda:
          '在V·IA中提问时，谁来回答？\n\n系统首先尝试主要提供商。如果失败或达到使用上限，会自动切换到备用链中的下一个——用户完全感知不到切换过程。\n\n"重新索引"按钮会用系统中最新发布的文档更新RAG搜索索引。',
      },
      modos: {
        titulo: '助手模式',
        ayuda:
          'V·IA根据不同需求以不同方式工作：\n\n· 生成 — 根据指令创建新内容。\n· 纠错 — 审查并改善现有文本。\n· 审计 — 验证文档的法规合规性。\n· 翻译 — 将内容适配到其他语言。\n\n您可以为每种模式分配不同的模型，以平衡速度、成本和质量。',
      },
      rag: {
        titulo: 'RAG 索引状态',
        ayuda:
          '什么是 RAG 索引？\n\n系统通过将文本转换为数学向量（嵌入）来搜索文档。该索引生成一次后可在每次查询中重复使用。\n\n· "仅变更" — 仅重新索引自上次索引以来修改的文档。速度快，不会耗尽 Gemini 配额。\n· "全部" — 从头重新索引所有文档。如果怀疑索引损坏或不完整时使用。\n\n自动重新索引在服务器启动时以及每 30 分钟在后台运行一次。',
        soloCambios: '仅变更',
        todo: '全部',
        reindexar: '重新索引',
        indexando: '索引中...',
        ultimaIndexacion: '上次索引',
        autoCada: '自动重新索引间隔',
        minutos: '分钟',
        ultimosCiclos: '最近的重新索引',
      },
    },
    notificaciones: {
      titulo: '通知偏好设置',
      evento: '事件',
      email: '邮件',
      push: '推送',
      inApp: '应用内',
      guardar: '保存偏好',
      guardado: '通知偏好已保存',
      canales: {
        titulo: '通知渠道',
        descripcion: '配置发送提供商，使工作流"通知"阶段能够到达外部收件人。',
        mailTitulo: '邮件 (Resend)',
        mailDescripcion: '通过 Resend 发送邮件通知',
        mailHabilitar: '启用邮件',
        mailFromEmail: '发件人邮箱',
        mailApiKey: 'Resend API 密钥',
        teamsTitulo: 'Microsoft Teams',
        teamsHabilitar: '启用 Teams',
        teamsWebhook: '入站 webhook URL',
        slackTitulo: 'Slack',
        slackHabilitar: '启用 Slack',
        slackWebhook: '入站 webhook URL',
        modoSandbox: '沙盒模式（不发送，仅记录日志）',
        modoSandboxDescripcion: '激活时，提供商仅记录日志而不发送。适用于安全部署。',
        cambiar: '更改',
        limpiar: '清除',
        credencialGuardada: '凭据已保存',
        credencialAusente: '无凭据',
        nuevoValor: '新值',
        guardado: '配置已保存',
        errorGuardar: '保存配置时出错',
        // ADR-027.3 (0658c): "测试通道" 按钮 + 内联审计跟踪键
        test: {
          botonProbar: '测试通道',
          enviando: '发送中...',
          exito: '✓ 消息发送成功',
          error: '✕ 错误：{detalle}',
          mensajeEnviadoEn: '发送于 {timestamp}',
          messageId: 'ID：{id}',
          reasonNoProvider: '凭证未配置',
          reasonProviderError: '提供商错误',
          reasonSandboxMode: '沙盒模式启用',
          confirmarTest: '向当前管理员邮箱发送测试消息？将发送真实消息（绕过沙盒模式）。',
        },
      },
    },
    secciones: {
      usuarios: '用户',
      roles: '角色与权限',
      procesos: '流程',
      modulos: '模块',
      via: '人工智能',
      configuracion: '设置',
      auditoria: '审计',
    },
    usuarios: {
      nombre: '姓名',
      email: '邮箱',
      rol: '角色',
      estado: '状态',
      ultimoAcceso: '最后访问',
      crear: '+ 新建用户',
      editar: '编辑',
      desactivar: '停用',
      activo: '活跃',
      inactivo: '停用',
      sinUsuarios: '无用户',
      gestionarUsuarios: '管理租户用户',
      nuevoUsuario: '新建用户',
      nombreCompleto: '全名',
      crearUsuario: '创建用户',
      creando: '创建中...',
      emailNombreObligatorios: '邮箱和姓名为必填项',
      creadoCorrectamente: '用户创建成功',
      desactivado: '用户已停用',
      confirmarDesactivar: '此用户将被停用。是否继续？',
      creado: '已创建',
      // 0577 + 0580 + 0583
      cambiarRolTitulo: '更改用户角色',
      cambiarRolUsuarioLabel: '用户',
      cambiarRolActual: '当前角色',
      cambiarRolNuevo: '新角色',
      cambiarRolHelp: '只有 SUPERADMIN_PLATFORM 可以分配 SUPERADMIN_PLATFORM。后端会验证。',
      guardarCambios: '保存更改',
      guardando: '保存中...',
      cancelar: '取消',
      readOnlyBanner: '只读视图。您的角色不允许邀请或修改用户。',
      copyPasswordTitulo: '用户已创建 - 复制临时密码',
      copyPasswordHelp:
        '未配置自动邮件。请复制此密码并安全地交给用户 {email}。此密码将不会再次显示。',
      copiar: '复制',
      cerrar: '关闭',
      passwordCopiada: '密码已复制到剪贴板',
      empresaReadOnly: '只读视图。您的角色不允许修改公司设置。',
      iaReadOnly: '只读视图。您的角色不允许修改 AI 设置。',
      // 0586
      cargosOrganigramaInfo:
        '治理角色（企业组织架构岗位）不是系统 RBAC 角色。它们在 Structure 模块中管理。',
      cargosOrganigramaLink: '前往 Structure',
      // 0600
      tuBadge: '（你）',
      superadminTenantBanner:
        '您只能查看您内部租户（VALOR·IA）的用户。要管理其他租户的用户，请前往 {link} 并使用 Impersonate。',
      superadminTenantBannerLink: 'Backoffice',
      gestionar: '管理',
      exportarCsv: '导出 CSV',
      exportando: '导出中...',
      importarCsv: '导入 CSV',
      importando: '导入中...',
      invitar: '+ 邀请',
      invitarTitulo: '邀请新用户',
      invitarRolLabel: '分配角色',
      invitarEnviar: '发送邀请',
      enviando: '发送中...',
      noUsuariosSeleccionados: '0 个用户',
      mostrandoCount: '显示 {from}-{to} / {total}',
      porPagina: '每页:',
      modalDatosBasicos: '基本资料',
      modalPermisos: '权限与访问',
      modalAcciones: '操作',
      modalEmailHelp: '此处无法更改电子邮件（影响登录）。',
      modalPersonaLabel: '组织结构图人员（M1 Structure）',
      modalSinVincular: '— 未关联 —',
      modalUltimoAcceso: '最后访问',
      modalSelfBanner: '您不能更改自己的角色，也不能停用/删除自己。只能编辑姓名和关联人员。',
      modalAdminOwnerBanner:
        'TENANT_ADMIN 不能修改 TENANT_OWNER。仅 TENANT_OWNER 或 SUPERADMIN_PLATFORM 可以。',
      resetearContrasena: '重置密码',
      eliminarUsuario: '删除用户',
      lockoutBloqueada: '账户已锁定',
      lockoutIntentos: '{n} 次失败尝试',
      lockoutMaximo: '5 次后自动锁定 15 分钟。',
      lockoutDesbloquearAuto: '将在 {min} 分钟后自动解锁。',
      lockoutTrasIntentos: '在 {n} 次失败尝试后。',
      desbloquear: '解锁',
      bulkSeleccionados: '已选择 {n} 个用户',
      bulkLimpiar: '清除',
      bulkActivar: '激活',
      bulkDesactivar: '停用',
      bulkEliminar: '删除',
      confirmar: '确认',
      confirmActivar: '激活用户',
      confirmDesactivar: '停用用户',
      confirmEliminar: '删除用户',
      personaVinculadaCount: '租户目录中有 {n} 人。',
    },
    roles: {
      titulo: '角色与权限',
      matrizPermisos: 'RBAC 角色权限矩阵',
      permiso: '权限',
    },
    procesos: {
      titulo: '流程',
      vistaGeneral: '租户流程地图概览',
      total: '总计',
      estrategicos: '战略',
      operativos: '运营',
      soporte: '支持',
      activos: '活跃',
      filtrar: '筛选：',
      todos: '全部',
      cargando: '加载流程中...',
    },
    modulos: {
      titulo: '模块',
      estadoModulos: '租户 V·IAnet 模块状态',
      soloPlatformPuedeActivar: '仅 VALOR·IA 平台可激活或停用模块。仅查看模式。',
      total: '总计',
      activos: '活跃',
      inactivos: '未激活',
      cargando: '加载模块中...',
      descriptions: {
        M1: '交互式组织架构图',
        M2: '流程地图',
        M3: '流程图',
        M4: '文档管理',
        M5: '智能搜索',
        M6: '版本控制与审批',
        M7: '审计',
        M8: '管理与RBAC',
        M9: '人工智能API',
        M10: '治理虚拟助手',
        M11: '治理仪表盘',
        M12: '流程数字化应用',
        M13: '内部沟通',
        M14: '移动应用',
        M15: '电子签名',
        M16: '请求与审批',
        M17: '多渠道通知',
        M18: '多语言支持',
        M19: 'ISO 31000 风险管理',
        M20: '内部帮助台和支持',
      },
      proximamente: '即将推出',
      activado: '已启用',
      desactivado: '已禁用',
      requiere: '需要',
      dependeDe: '依赖于',
    },
    i18n: {
      titulo: '语言管理',
      descripcion: '系统翻译键在3种支持语言中的概览',
      clavesTotal: '总键数',
      sinTraducirEN: '缺少英文翻译',
      sinTraducirZH: '缺少中文翻译',
      buscar: '按键名或文本搜索...',
      soloSinTraducir: '仅显示未翻译',
      clave: '键',
      falta: '缺少',
      mostrando: '显示',
      sinResultados: '无结果',
    },
    via: {
      titulo: '人工智能',
      configuracion: 'V·IA 配置及令牌消耗',
      proveedor: 'AI 提供商',
      modelo: '模型',
      consumo: '令牌消耗',
      limite: '月度限额',
      usados: '已使用令牌',
      restantes: '剩余令牌',
      consumoMes: '本月消耗',
      consumoHistorico: '历史消耗',
      configuracionModelos: '模型配置',
      proveedorTraducciones: '翻译服务商',
      proveedorTraduccionesAuto: '自动（本地 Gemma → 备选）',
      proveedorTraduccionesHint: '创建或编辑内容时，自动将数据翻译为英文和中文。',
      apiKeyPropia: '自有 API Key（可选）',
      apiKeyDescripcion: '如果提供，将使用此密钥代替 V·IANet 共享密钥。',
      guardarConfigIA: '保存AI配置',
      configGuardada: 'AI配置已保存',
      cargando: '加载AI配置中...',
      utilizado: '已使用',
      temperatura: '温度',
      maxTokens: '最大令牌数',
    },
    config: {
      titulo: '设置',
      descripcion: '租户、品牌和模块设置',
      identidadMarca: '品牌标识',
      general: '常规',
      modulos: '模块',
      marca: '品牌',
      nombreMarca: '品牌名称',
      fuente: '字体',
      colores: '颜色',
      primario: '主色',
      secundario: '次色',
      acento: '强调色',
      vistaPrevia: '预览',
      logos: '标志',
      logoPrincipal: '主标志',
      logoHeader: '页头标志',
      favicon: '网站图标',
      guardarCambios: '保存更改',
      archivoActualizado: '文件已更新',
      configGuardada: '设置已保存',
      cargando: '加载设置中...',
      configuracionGeneral: '常规设置',
      idioma: '语言',
      zonaHoraria: '时区',
      dominio: '自定义域名',
      plan: '计划',
      modulosActivos: '活跃模块',
      guardarModulos: '保存模块',
      guardando: '保存中...',
      errorAlSubirArchivo: '文件上传错误',
    },
    auditoria: {
      titulo: '审计',
      registroActividad: '系统活动日志',
      entidad: '实体',
      accion: '操作',
      desde: '从',
      hasta: '至',
      todas: '全部',
      fecha: '日期',
      usuario: '用户',
      evento: '事件',
      detalle: '详情',
      cargando: '加载中...',
      sinRegistros: '无记录',
      anterior: '上一页',
      siguiente: '下一页',
      sistema: '系统',
    },

    // 0658a — 工作流通知模板 (ADR-027.1)
    workflow: {
      plantillas: {
        tituloEditor: '模板编辑器',
        tabIn_app: '应用内',
        tabEmail: '邮件',
        tabTeams: 'Teams',
        tabSlack: 'Slack',
        labelAsunto: '主题',
        labelCuerpo: '内容',
        idiomaEs: '西班牙语',
        idiomaEn: '英语',
        idiomaZh: '中文',
        variablesDisponibles: '可用变量',
        placeholderAsunto: '例如: {{artefactoTitulo}} 的新版本',
        placeholderCuerpo: '消息正文。使用 {{variables}} 进行个性化。',
        botonGuardar: '保存模板',
        botonActiva: '模板生效中',
        botonAnadirEditor: '编辑模板',
        actionTooltipNotificar: '自动处理通知（无需批准）',
        selectOptionRevisar: '审核',
        selectOptionAprobar: '批准',
        selectOptionNotificar: '通知',
        cargando: '加载中...',
        cerrar: '关闭',
      },
    },
  },

  // Tenant Selector
  tenantSelector: {
    subtitle: '选择您要访问的组织',
    selectTenant: '可用组织',
  },

  // Onboarding Wizard
  onboarding: {
    steps: ['欢迎', '公司数据', '标志和品牌', '初始用户', '完成'] as string[],
    stepLabel: (current: number, total: number) => `第 ${current} 步，共 ${total} 步`,
    welcomeTitle: '欢迎使用 V·IANet Governance',
    welcomeDesc:
      '在以下步骤中，我们将在平台上设置您的组织。本向导将引导您完成基本数据、视觉标识和初始用户配置。',
    companyTitle: '公司数据',
    companyName: '公司名称',
    companyNamePlaceholder: '我的公司有限公司',
    industry: '行业 / 领域',
    industryPlaceholder: '科技、金融、能源...',
    country: '国家',
    countryPlaceholder: '中国',
    employees: '员工数',
    brandTitle: '标志和品牌',
    logo: '组织标志',
    dropLogo: '拖拽图片或点击选择',
    primaryColor: '主色',
    secondaryColor: '辅色',
    accentColor: '强调色',
    usersTitle: '初始用户',
    usersDesc: '邀请组织的首批用户。您可以稍后从管理面板添加更多用户。',
    email: '邮箱',
    role: '角色',
    completeTitle: '配置完成',
    completeDesc: '您的组织已准备就绪。您可以开始使用 V·IANet Governance 并配置公司治理模块。',
    goToPanel: '前往管理面板',
  },

  // Backoffice
  backoffice: {
    titulo: '后台 — 平台管理',
    gestionTenants: '平台租户',
    descripcion: 'V·IANet Governance 上所有已注册组织的集中管理。',
    crearTenant: '创建租户',
    usingPlaceholder: '无法连接到服务。显示示例数据。',
    columns: {
      nombre: '名称',
      plan: '方案',
      usuarios: '用户',
      docs: '文档',
      estado: '状态',
      acciones: '操作',
    },
    addAdmin: {
      btn: '+ 管理员',
      btnEliminar: '删除',
      titulo: '添加管理员到',
      subtitulo: '将在此租户中创建具有CEO角色的用户。您将获得一个临时密码以提供给客户管理员。',
      email: '管理员邮箱',
      emailPlaceholder: 'admin@company.com',
      nombre: '全名',
      nombrePlaceholder: 'John Doe',
      cancelar: '取消',
      crear: '创建管理员',
      creando: '创建中…',
      exitoTitulo: '管理员已创建！',
      exitoDescripcion: '复制临时密码并交给客户。出于安全原因，您将无法再次查看。',
      passwordLabel: '临时密码',
      copiarPassword: '复制',
      copiado: '已复制！',
      cerrar: '关闭',
      confirmEliminarTitulo: '删除租户？',
      confirmEliminarDesc: (nombre: string) =>
        `您即将删除"${nombre}"及其所有数据（用户、AI配置、审计日志）。此操作无法撤消。`,
      confirmEliminarBtn: '是的，删除',
    },
    impersonate: {
      btn: '以此身份登录',
      banner: '您正在模拟 {tenant}(原始会话:{email})·',
      bannerVolver: '返回我的账户',
      confirmTitulo: '以此租户管理员身份登录？',
      confirmDesc: (nombre: string) =>
        `您将进入"${nombre}"租户账户1小时。所有操作都将以您的原始身份记录在审计日志中。`,
      confirmBtn: '是的,登录',
      error: '无法启动模拟会话。',
    },
    wizard: {
      titulo: '创建新租户',
      subtitulo: '完成 3 个步骤，在平台上注册新组织',
      step0Label: '基本信息',
      step1Label: '配置',
      step2Label: '品牌',
      nombre: '组织名称',
      nombrePlaceholder: '集团名称、公司名称...',
      slugHint: '组织的唯一标识符，仅限字母、数字和连字符。',
      plan: '方案',
      maxUsuarios: '最大用户数',
      modulos: '已启用模块',
      modulosSeleccionados: '个模块已选择',
      idioma: '默认语言',
      formatoFecha: '日期格式',
      zonaHoraria: '时区',
      colorPrimario: '主色',
      colorSecundario: '辅色',
      colorAcento: '强调色',
      fuente: '字体',
      preview: '预览',
      exitoTitulo: '租户创建成功！',
      exitoDescripcion: '该组织已在平台上注册。',
      volverBackoffice: '返回后台',
      anterior: '上一步',
      siguiente: '下一步',
      crear: '创建租户',
      creando: '创建中...',
    },
  },

  // Importar documento
  importar: {
    titulo: '导入文档',
    descripcion: '上传 .docx 或 .pdf 文件以将其内容导入系统',
    arrastrar: '将文件拖至此处或点击选择',
    oSeleccionar: '支持格式：.docx、.pdf（最大 50 MB）',
    procesando: '处理文件中...',
    previsualizacion: '预览',
    confirmar: '确认导入',
    cancelar: '取消',
    importando: '导入文档中...',
    creandoDocumento: '正在数据库中创建文档、版本和结构',
    exito: '文档导入成功',
    importarOtro: '导入另一个',
    verDocumento: '查看文档',
    reintentar: '重试',
    metadatos: '文档元数据',
    codigo: '编号',
    nombre: '名称',
    tipoDocumental: '文档类型',
    proceso: '流程',
    edicion: '版本',
    archivo: '文件',
    seleccione: '请选择...',
    autoDetectar: '从编号自动检测',
    estructuraDetectada: '检测到的结构',
    secciones: '章节',
    bloques: '块',
    seccionVacia: '（空章节）',
    advertencias: '警告',
    completarCampos: '请填写所有必填字段',
    soloDocxPdf: '仅支持 .docx 和 .pdf 文件',
    errorParsear: '解析文件时出错',
    errorConfirmar: '确认导入时出错',
  },

  // Common
  common: {
    dictado: {
      dictar: '语音输入',
      detener: '停止',
      transcribiendo: '转录中…',
      noSoportado: '您的浏览器不支持录音',
      sinPermiso: '无法访问麦克风',
      error: '无法转录',
    },
    persona: {
      cargando: '正在加载人员…',
      noEncontrada: '未找到人员',
      puestoUnknown: '无有效职位',
    },
    guardar: '保存',
    cancelar: '取消',
    cerrar: '关闭',
    eliminar: '删除',
    editar: '编辑',
    crear: '创建',
    buscar: '搜索...',
    cargando: '加载中...',
    error: '错误',
    exito: '成功',
    confirmar: '确认',
    volver: '返回',
    siguiente: '下一步',
    anterior: '上一步',
    guardando: '保存中...',
    acciones: '操作',
    ver: '查看',
    nombre: '名称',
    tipo: '类型',
    estado: '状态',
    codigo: '编号',
    fecha: '日期',
    searchPlaceholder: '搜索...',
    rejectionReason: '请输入拒绝原因...',
    documentName: '文档名称',
    nodeName: '节点名称',
    nodeDescription: '节点描述',
    noPhase: '无阶段',
    selectPosition: '选择职位...',
    selectDocument: '选择文档...',
    selectProcess: '选择流程...',
    examplePolicy: '例如：质量政策',
    processDescription: '流程描述...',
    processScope: '流程范围...',
    exampleChangelog: '例如：更新第5.3节以纳入新的审批流程...',
    processCode: '流程编号（例如：PE.21）',
    companyName: '公司名称',
    exampleCode: 'PE.21.NC.01',
    basePath: 'C:\\文档\\流程地图',
    conditionExample: '例如：已批准？',
    descripcion: '描述',
    enDesarrollo: '此功能正在开发中',
    loadingDocument: '加载文档中...',
    documentNotFound: '未找到文档',
    discard: '放弃',
    closeEditor: '关闭编辑器',
    newEditionBtn: '+ 新版本',
    documentSaved: '文档已保存',
    errorSaving: '保存错误：',
    changesDiscarded: '更改已放弃',
    statusUpdated: '状态更新成功',
    newEditionCreated: '新版本已创建',
    errorNewEdition: '创建新版本时出错',
    discardChanges: '放弃更改',
    discardWarning: '自上次保存以来的所有更改都将丢失。此操作无法撤消。',
    autoSave: '自动保存：',
    noContent: '此文档暂无内容。',
    processing: '处理中...',
    creating: '创建中...',
    selectDocFromList: '从列表中选择文档以在此处查看',
    style: '样式...',
    page: '页',
    nuevaEdicionBtn: '+ 新版本',
    errorTransicion: '转换错误',
    errorDescartar: '放弃更改时出错',
    errorCargarDocumento: '加载文档时出错',
    errorDesconocido: '未知错误',
    noTieneContenido: '此文档尚无内容。',
    descartarCambiosDialogTitle: '放弃更改',
    descartarCambiosDialogDesc: '编辑器中所有未保存的更改将丢失。此操作无法撤销。',
    accionNoValida: (accion: string, estado: string) => `操作"${accion}"对当前状态"${estado}"无效`,
    expandir: '展开',
    negrita: '粗体',
    cursiva: '斜体',
    titulo1: '标题1',
    titulo2: '标题2',
    titulo3: '标题3',
    listaConVinetas: '无序列表',
    listaNumerada: '有序列表',
    insertarTabla: '插入表格',
    deshacer: '撤销',
    rehacer: '重做',
    viaAsistente: 'V·IA 助手',
    sinSeleccion: '--',
    pagination: {
      anterior: '上一页',
      siguiente: '下一页',
      pageOf: (page: number, total: number) => `第 ${page} 页，共 ${total} 页`,
    },
    estados: {
      activo: '启用',
      inactivo: '停用',
    },
  },

  // Role labels
  roles: {
    CEO: '总经理',
    COO: '运营总监',
    CTO: '技术总监',
  },

  // Structure module (M1)
  structure: {
    // Landing page
    title: '组织结构',
    description: '组织架构图、职位、层级关系和职能关系。定义组织架构和公司治理机构。',
    puestosTitle: '职位目录',
    personasTitle: '人员目录',
    tooltips: {
      departamentosArbol: {
        titulo: '组织结构',
        ayuda:
          '表示租户的组织单位层级。\n\n· 点击任意节点查看详情和分配的人员。\n· 灰色节点对应非活跃单位。\n· 结构反映了在管理中配置的组织模型。',
      },
      adminPersonas: {
        titulo: '人员管理',
        ayuda:
          '租户的人员、职位和角色目录。\n\n每个人可以担任多个职位。职位属于定义治理流程中职责的职能角色。\n\n此处的更改会影响分配给流程和文档的负责人。',
      },
      puestos: {
        titulo: '职位目录',
        ayuda:
          '组织中定义的所有职位列表。\n\n每个职位属于一个职能角色，可以分配一个或多个人员。职位是流程和文档中责任分配的单位。',
      },
      personas: {
        titulo: '人员目录',
        ayuda:
          '系统中注册的所有人员及其职位和部门。\n\n使用筛选器按部门、角色或状态查找人员。点击人员查看其完整档案及在流程和文档中的职责。',
      },
      glosario: {
        titulo: '企业术语表',
        ayuda:
          '治理系统的术语和定义库。\n\n术语表中的术语用于文档、流程和说明书，以确保整个组织使用共同的语言。',
      },
      misSolicitudes: {
        titulo: '我的申请',
        ayuda:
          '您创建的或参与作为审批人或相关方的申请。\n\n您可以查看每个申请的状态、待处理步骤以及已执行的操作历史记录。',
      },
    },
    // FichaPersonalViewer (FP/FR)
    ficha: {
      tipoBadgeFP: '职位说明书',
      tipoBadgeFR: '职能角色',
      guardando: '保存中…',
      guardado: '已保存 ✓',
      sinRelaciones: '（未定义层级关系）',
      sinRoles: '（未分配治理角色）',
      workflow: {
        enviarRevision: '提交审核',
        aprobar: '批准',
        iniciarFirma: '开始签署',
        iniciandoFirma: '开始中…',
        reintentar: '重试',
        errorFirma: '启动签署时出错',
        firmaSolicitada: '签署请求已发送',
        firmaSolicitadaN: (n: number) => `已发送给 ${n} 名审批人`,
      },
    },
    descriptions: {
      orgChart: '可视化并编辑组织层级结构',
      organizations: '管理企业集团实体',
      people: '人员目录和人才管理',
      governance: '治理分析和管理机构',
    },
    // Discipline tabs
    tabs: {
      orgChart: '组织架构图',
      organizations: '组织',
      people: '人员',
      governance: '治理',
      analytics: '分析',
      positions: '岗位',
      departments: '部门',
      admin: '管理',
    },
    analytics: {
      title: '人员分析',
      comingSoon: '即将推出',
      tabs: { governance: '治理', business: '业务' },
      governance: {
        coverageRate: '角色覆盖率',
        vacancies: '空缺',
        criticalVacancies: '关键角色空缺',
        activeRoles: '活跃角色',
        inPositions: '个职位',
        busFactor: '巴士因子',
        personsMultiRole: '人拥有2+角色',
        jobDescriptions: '职位描述',
        completed: '已完成',
        occupancyRate: '占用率',
        withoutPosition: '无职位',
        departments: '部门',
        withActivePeople: '有活跃人员',
        roleConcentration: '角色集中度（巴士因子）',
        rolesByType: '按类型的治理角色',
        coverageByLevel: '按层级的覆盖率',
        alerts: '治理警报',
        detailTable: '人员详情',
        clearFilters: '清除筛选',
        activeFilters: '活跃筛选',
        noResults: '未找到符合筛选条件的人员。',
        noData: '无数据',
        noDataDesc: '无可用的治理数据。',
        fichasPuesto: '岗位说明书',
        noPuesto: '无岗位人员',
        busFactorChart: '角色集中度（关键人风险）',
        loading: '正在加载治理数据...',
        error: '加载治理数据时出错。',
      },
      business: {
        title: '业务',
        totalPeople: '活跃人员',
        recentHires: '最近3个月',
        evolution: '入职趋势',
        monthlyHires: '月度入职',
        cumulative: '累计',
        companies: '企业',
        sites: '工作中心',
        positions: '职位',
        countries: '国家',
        departments: '部门',
        byDepartment: '按部门',
        byPosition: '按职位',
        byCompany: '按企业',
        bySite: '按工作中心',
        byCountry: '按国家',
        directory: '目录',
        filters: '筛选',
        clearFilters: '清除筛选',
      },
      unified: {
        title: '人员分析',
        loading: '正在加载人员分析...',
        coverageRoles: '覆盖率',
        vacancies: '空缺',
        busFactor: '总线因子',
        activeRoles: '角色',
        jobDescriptions: '岗位说明',
        occupancy: '占用率',
        activePeople: '人员',
        companies: '公司',
        positions: '岗位',
        departments: '部门',
        countries: '国家',
        recentHires: '近3月入职',
        busFactorChart: '总线因子',
        rolesByType: '按类型角色',
        evolution: '演变',
        byDepartment: '按部门',
        coverageByLevel: '按级别覆盖',
        alerts: '警报',
        vacancyDistribution: '空缺',
        byCompany: '按公司',
        bySite: '按中心',
        byCountry: '按国家',
        detailTable: '人员目录',
        clearFilters: '清除筛选',
        superior: '上级',
        byPosition: '按岗位',
      },
    },
    // Org chart
    orgChart: {
      title: '企业组织架构图',
      vertical: '纵向',
      horizontal: '横向',
      exportPdf: '导出PDF',
      exporting: '导出中...',
      fullscreen: '全屏',
      exitFullscreen: '退出',
      editOrgChart: '编辑组织架构',
      saveAndExit: '保存并退出',
      discardChanges: '放弃更改',
      discardChangesConfirm: '放弃所有组织架构更改？此操作无法撤销。',
      closeEditMode: '关闭',
      loadingOrgChart: '加载组织架构图...',
      editMode: '编辑',
      editHelp: '拖动节点以移动。连接端口以创建关系。右键点击查看更多选项。',
      resetLayout: '重置自动布局',
      backToFull: '返回完整组织架构',
      newPuesto: '+ 新建岗位',
      totalPuestos: '岗位总数',
      vacantes: '空缺',
      personasAsignadas: '已分配人员',
      editModeActive: '编辑模式已激活',
      semanticZoom: '语义缩放',
      errorLoadPuesto: '加载岗位时出错',
      close: '关闭',
      viewPersonProfile: '查看完整资料',
      layerNone: '无图层',
      layerGovernance: '治理角色',
      layerVacancies: '空缺',
      layerLevels: '级别',
      legend: '图例',
      vacantCritical: '空缺关键角色',
      viewInOrgChart: '在组织图中查看',
      searchPlaceholder: '搜索人员或职位...',
      legendNiveles: '级别',
      noResults: '无结果',
      // 0651: WorkflowBar + 组织架构图查看器字符串（从硬编码迁移）。
      loadingWorkflow: '正在加载工作流…',
      historyVersions: '版本历史',
      noVersionsYet: '暂无版本。',
      viewHistory: '查看版本历史',
      historyButton: (n: number) => `历史 (${n})`,
      editionNumber: (n: number) => `组织架构图第 ${n} 版`,
      editButtonDisabledTooltip: '请先创建新版本 — 只能编辑草稿版本。',
    },
    // Detail drawer
    drawer: {
      info: '信息',
      nivel: '级别',
      departamento: '部门',
      organizacion: '组织',
      assignedPerson: '分配人员',
      vacant: '空缺',
      chainOfCommand: '指挥链',
      subordinates: '下属',
      moreItems: (n: number) => `+${n} 更多...`,
      governanceRoles: '治理角色',
      viewFicha: '查看岗位说明书',
      noFicha: '无说明书',
      viewInOrgChart: '在组织图中查看',
    },
    fichaLabel: '说明书',
    // Context menu & floating toolbar
    editPuesto: '编辑岗位',
    assignPerson: '分配人员',
    createSubordinate: '创建下属',
    createRelation: '创建关系',
    deletePuesto: '删除岗位',
    confirmDeletePuesto: '确定要删除此岗位？此操作无法撤销。',
    errorDeletePuesto: '删除岗位时出错',
    // Edge popover
    deleteRelation: '删除关系',
    // Relation modal
    newRelation: '新建关系',
    relationType: '关系类型',
    solidRelation: '实线（直接层级）',
    dottedRelation: '虚线（间接汇报）',
    functionalRelation: '职能型',
    projectRelation: '项目型（临时）',
    isPrimaryRelation: '是否主要关系',
    createRelationBtn: '创建关系',
    // Create puesto modal
    newPuestoTitle: '新建岗位',
    titleRequired: '标题为必填项',
    errorCreatePuesto: '创建岗位时出错',
    tituloLabel: '标题',
    codigoLabel: '编号',
    nivelLabel: '级别',
    organizacionLabel: '组织',
    departamentoLabel: '部门',
    reportsToLabel: '汇报给',
    assignedPersonLabel: '分配人员（可选）',
    selectOption: '选择',
    noneOption: '无',
    noAssignment: '未分配',
    creating: '创建中...',
    createPuesto: '创建岗位',
    // Person selector modal
    assignPersonTitle: '分配人员',
    puestoLabel: '岗位',
    searchByNameOrEmail: '按姓名或邮箱搜索...',
    noResults: '无结果',
    errorAssignPerson: '分配人员时出错',
    // PuestoNode
    vacantLabel: '空缺',
    ocupadoLabel: '在岗',
    soloVacantes: '仅空缺',
    puestoDetail: {
      loading: '加载职位中...',
      notFound: '未找到职位',
      goBack: '返回',
      viewInOrgChart: '在组织架构中查看',
      fichaTitle: '职位档案',
      openDocument: '打开文档',
      noFicha: '此职位没有关联的特征档案。',
      createFicha: '创建职位档案',
      creatingFicha: '创建中...',
    },
    // Organizaciones
    orgs: {
      title: '组织管理',
      newOrg: '+ 新建组织',
      loadingOrgs: '加载组织中...',
      noOrgs: '暂无注册组织。',
      editOrg: '编辑组织',
      newOrgTitle: '新建组织',
      name: '名称',
      shortName: '简称',
      type: '类型',
      departments: '部门',
      positions: '岗位',
      status: '状态',
      active: '活跃',
      inactive: '停用',
      view: '查看',
      nameRequired: '名称为必填项',
      confirmDeleteOrg: '删除此组织？所有部门和岗位将被删除。',
      holding: '控股公司',
      empresa: '企业',
      filial: '子公司',
      sucursal: '分公司',
      division: '事业部',
      unidadNegocio: '业务单元',
      ute: '联合体',
      jointVenture: '合资企业',
      concesion: '特许经营',
      // Detail view
      back: '返回',
      departmentTab: (n: number) => `部门 (${n})`,
      positionTab: (n: number) => `岗位 (${n})`,
      newDept: '+ 新建部门',
      editDept: '编辑部门',
      newDeptTitle: '新建部门',
      parentDept: '上级部门',
      noneRoot: '无（根级别）',
      noDepts: '暂无部门',
      confirmDeleteDept: '删除此部门？',
      newPosition: '+ 新建岗位',
      editPosition: '编辑岗位',
      newPositionTitle: '新建岗位',
      superior: '上级',
      none: '无',
      noDept: '无部门',
      noPositions: '暂无岗位',
      confirmDeletePosition: '删除此岗位？',
      person: '人员',
      parentOrg: '母公司',
      nifCif: '税号',
      country: '国家',
      parentOrgLabel: '母公司',
      subsidiaries: '子公司',
      vistaArbol: '树形',
      vistaTabla: '表格',
    },
    // Gobernanza
    governance: {
      title: '治理分析',
      vacanciesTab: '空缺岗位',
      matrixTab: '责任矩阵',
      impactTab: '影响分析',
      vacanciesCount: (n: number) => `${n} 个岗位无人员分配`,
      noVacancies: '无空缺。所有岗位已满员。',
      matrixDesc: (procs: number, puestos: number) => `${procs} 个流程 × ${puestos} 个负责岗位`,
      errorLoadMatrix: '无法加载责任矩阵。',
      noDataMatrix: '责任矩阵数据不足。',
      processCol: '流程',
      selectPuesto: '选择分析岗位',
      selectPuestoPlaceholder: '选择岗位...',
      analyzing: '分析中...',
      analyzeImpact: '分析影响',
      analyzedPuesto: '分析岗位',
      directSubordinates: (n: number) => `直接下属 (${n})`,
      affectedProcesses: (n: number) => `受影响流程 (${n})`,
      affectedDocuments: (n: number) => `受影响文档 (${n})`,
      governanceRoles: (n: number) => `治理角色 (${n})`,
      noSubordinates: '无下属',
      noProcesses: '无关联流程',
      noDocuments: '无关联文档',
      impactHint: '选择一个岗位并点击"分析影响"以查看删除该岗位的完整分析。',
      titulo: '标题',
      persona: '人员',
      noGovernanceRoles: '未分配治理角色',
      actions: '操作',
      viewInOrgChart: '在组织架构中查看',
      // 治理机构 (F2.10+F2.11+F2.12)
      organosTab: '治理机构',
      organosEmpty: '未登记治理机构',
      organosLoading: '正在加载治理机构...',
      organosErrorLoad: '无法加载治理机构',
      miembrosActivos: (n: number) => `${n} 名在任成员`,
      sinMiembros: '未登记成员',
      estadoActivo: '在任',
      estadoCesado: '已离任',
      cargoCol: '职位',
      nombreCol: '姓名',
      desdeCol: '自',
      hastaCol: '至',
      estadoCol: '状态',
    },
    admin: {
      title: '管理',
      personas: '人员',
      puestos: '岗位',
      departamentos: '部门',
      roles: '治理角色',
      organizaciones: '组织',
      centros: '中心',
      niveles: '级别',
      catalogos: '目录',
      usuarios: '用户 ↔ 人员',
      glosario: '术语表',
      organos: '治理机构',
      newPerson: '新建人员',
      editPerson: '编辑人员',
      newPosition: '新建岗位',
      editPosition: '编辑岗位',
      newDepartment: '新建部门',
      editDepartment: '编辑部门',
      newRole: '新建角色',
      newOrganization: '新建组织',
      newSite: '新建中心',
      newLevel: '新建级别',
      newCatalog: '新建目录',
      // 0455 — admin 顶层
      activo: '启用',
      inactivo: '停用',
      // 0458 — i18n 第二轮扫描:表格列标题
      acciones: '操作',
      fichaFR: 'FR 档案',
      // 0473 — RF / RG 界面统一（按钮和切换开关通用文本）
      mostrarInactivos: '显示停用',
      fichaCrear: '创建 FR',
      fichaVer: '查看 FR',
      rolesContador: '角色',
      // 0461 — i18n 第三轮扫描:残留标题/标签 (estado 已存在)
      tipo: '类型',
      nif: '税号',
      cargo: '职位',
      desde: '自',
      hasta: '至',
      entidad: '实体',
      miembros: '成员',
      // 0457 — admin 通用工具提示
      tooltips: {
        editar: '编辑',
        desactivar: '停用',
        cesar: '解除',
        eliminar: '删除',
        fichaAsignada: '已关联 FR',
        sinFichaRol: '无 FR',
        toggleActivar: '启用',
        toggleDesactivar: '停用',
        verFichaRol: '查看 FR',
        rolPlataforma: '平台角色',
        desactivarOrgano: '停用治理机构',
        cesarMiembro: '解除成员',
        eliminarMiembro: '删除成员',
        // 0471 M12 — 各 Admin 标签页的说明性工具提示(共享外壳)
        adminPersonas: {
          titulo: '人员目录',
          ayuda:
            '租户的人员目录,包含员工、合作者和外部人员,并可追溯其职位、部门和在职状态。\n\n每个人员可以同时担任多个职位。此处的变更会反映在流程、文件和治理机构的责任人分配中。',
        },
        adminPuestos: {
          titulo: '职位目录',
          ayuda:
            '组织中所有已定义职位的登记簿。每个职位归属于一个职能角色、一个部门和一个组织,并可在不同时间段分配给多位人员。\n\n职位是在流程和文件中分配职责的最小单位。',
        },
        adminDepartamentos: {
          titulo: '部门',
          ayuda:
            '构成组织的组织单元。每个部门隶属于一个组织,可以有上级部门(层级关系)和一个负责职位。\n\n单元类型(可按租户配置)用于将部门划分为事业部、区域、单元、团队等。',
        },
        adminNivelesPuesto: {
          titulo: '职位级别',
          ayuda:
            '职位层级目录(初级、高级、经理、总监等)。每个职位归属于一个级别,用于确定薪酬区间、职责复杂度和职业发展路径。\n\n使用箭头对级别进行重新排序,以体现组织内部的职业晋升路径。',
        },
        adminRolesFuncionales: {
          titulo: '职能角色',
          ayuda:
            '组织的职能角色目录。用于描述职位需要履行的职能、胜任能力和职责。\n\n每个职位继承自一个职能角色。角色的 FR 档案作为职位档案(FP)的参考模板。',
        },
        adminRolesGobernanza: {
          titulo: '治理角色',
          ayuda:
            '租户的治理角色目录(DPO、合规官、风险责任人等),包含关联职位和 FG 档案。\n\n区分全局角色与按流程角色(从流程档案中管理)。系统角色不可删除。',
        },
        adminTiposUnidad: {
          titulo: '组织单元类型',
          ayuda:
            '按租户配置的组织单元类型目录,用于对部门进行分类:事业部、区域、单元、团队等。\n\n如果类型已关联部门则不可删除。在组织结构图和选择器中按"顺序"字段排序。',
        },
        adminCatalogos: {
          titulo: '目录',
          ayuda:
            '按租户配置的辅助目录(合同类型、劳动类别、中心类型等)。\n\n每个目录定义表单和列表中允许的取值。系统目录不可删除;自定义目录可以删除,除非已有关联记录。',
        },
        adminCentros: {
          titulo: '中心',
          ayuda:
            '租户的物理中心和办公地点登记(办公室、工厂、仓库、分支机构)。\n\n每个中心隶属于一个组织,并保存地址、联系方式和地理位置。用于有地域范围的职位、项目和流程分配。',
        },
        adminOrganos: {
          titulo: '治理机构',
          ayuda:
            '租户的治理机构(董事会、管理委员会、审计委员会等),包含其成员及职务。\n\n每个机构隶属于一个集团实体,并保留成员任命和解除的历史记录以满足可追溯性要求。',
        },
        adminOrganizaciones: {
          titulo: '组织',
          ayuda:
            '集团公司下的法律实体(公司、合资企业、分支机构),以股权树方式组织,支持交叉持股。\n\n每个组织保存其税号、注册地址及与其他实体的关系。它是职位、中心和流程的组织结构根节点。',
        },
        adminGlosario: {
          titulo: '公司术语表',
          ayuda:
            '治理体系术语与定义的知识库。术语用于文件、流程和档案中,确保整个组织使用统一的语言。\n\n每个术语可包含同义词、分类以及"不翻译"标记,适用于专有名词或技术缩写。',
        },
        adminUsuariosPersonas: {
          titulo: '用户 ↔ 人员',
          ayuda:
            '将每个平台用户与其在人员目录中的档案关联。\n\n只有完成关联,用户才能在流程、文件和治理机构中显示为责任人。同时也可为用户分配平台角色(管理员、编辑者、阅读者)。',
        },
        adminTiposContrato: {
          titulo: '合同类型',
          ayuda:
            '按租户配置的劳动合同类型目录(无固定期限、定期、实习、商务合作等)。\n\n在人员的劳动资料中进行分配。当存在使用该类型的在职人员时,不能停用该类型。',
        },
        adminCategoriasLaborales: {
          titulo: '劳动类别',
          ayuda:
            '按租户配置的劳动类别目录(技术、行政、管理等),依据适用的集体合同而定。\n\n在人员的劳动资料中进行分配。当存在使用该类别的在职人员时,不能停用该类别。',
        },
      },
      // 0471 M12 — admin 组件间共享的列
      columns: {
        codigo: '代码',
      },
      // 0457 — 各组件占位文本
      organosPlaceholders: {
        nombre: '董事会 of...',
        cargo: '唯一管理人、董事、秘书...',
      },
      departamentosPlaceholders: {
        codigo: '例如:DEP-001',
      },
      usuariosPlaceholders: {
        busqueda: '按姓名、代码或邮箱搜索...',
      },
      // 0455 — 级别弹窗
      nivelModal: {
        title: '岗位级别',
        errorRequired: '代码和名称为必填项',
      },
      // 0455 — 职能角色面板（2 个标签页：数据 + Tiptap 档案）
      rolFuncionalPanel: {
        nuevoRol: '新建职能角色',
        tabDatos: '数据',
        tabFicha: '档案',
        fichaDeshabilitada: '请先保存基本数据',
        cancelar: '取消',
        guardar: '保存',
        saving: '保存中...',
        fichaNoExiste: '此角色尚未关联 FR 档案。',
        crearFicha: '创建职能角色档案',
        creandoFicha: '创建中...',
        fichaCreada: '档案已创建',
        fichaGuardada: '档案已保存',
        errorCargarFicha: '加载档案时出错',
        fichaMision: '角色使命',
        fichaFunciones: '职责与任务',
        fichaCompetenciasTecnicas: '技术能力',
        fichaCompetenciasTransversales: '通用能力',
        fichaFormacion: '所需学历',
        fichaExperiencia: '所需经验',
        fichaCertificaciones: '强制认证',
        fichaCondiciones: '角色条件',
      },
      noPermission: '您没有权限访问此部分',
      organizations: '组织',
      sites: '中心',
      levels: '岗位级别',
      catalogs: '目录',
      // AdminPuestos 列与筛选 (B·I18N.3 — 394)
      colCodigo: '编码',
      colTitulo: '职称',
      colDescripcion: '描述',
      catalogosContador: '目录',
      glosarioContador: '术语',
      tuoContador: '单位类型',
      departamentosContador: '部门',
      organizacionesContador: '组织',
      organosContador: '治理机构',
      colNivel: '级别',
      colDepartamento: '部门',
      colOrganizacion: '组织',
      colPlantilla: '编制',
      colVacante: '空缺',
      colVacantes: '空缺',
      personasTitle: '人员',
      newPersona: '新增人员',
      colNombre: '姓名',
      colEmail: '邮箱',
      colTipo: '类型',
      colEstado: '状态',
      colPuesto: '岗位',
      countLabel: '人员',
      todosLosNiveles: '所有级别',
      todosLosDepartamentos: '所有部门',
      nuevoPuesto: '新增岗位',
      vacanteSingular: (n: number) => `${n} 个空缺`,
      vacantesPluralN: (n: number) => `${n} 个空缺`,
      countPuestos: '岗位',
      sections: {
        identification: '身份信息',
        contact: '联系方式',
        employment: '就业数据',
        organization: '组织',
        notes: '备注',
        levels: '职位级别',
      },
      save: '保存',
      cancel: '取消',
      saved: '保存成功',
      error: '保存失败',
      confirmDeactivate: '确认停用此人员？',
      search: '搜索...',
      noResults: '无结果',
      required: '必填字段',
      vacante: '空缺',
      ocupado: '在岗',
      comingSoon: '即将推出',
      linkUser: '关联',
      unlinkUser: '取消关联',
      notLinked: '（未关联）',
      linked: '已关联',
      tiposUnidad: '单位类型',
      newTipoUnidad: '新建单位类型',
      editTipoUnidad: '编辑单位类型',
      nombreCorto: '简称',
      orden: '排序',
      color: '颜色',
      esDefault: '默认',
      cantDeleteTipoConDeptos: '无法删除：存在关联的部门',
      confirmDeleteTipo: '删除此组织单位类型？',
      rolesFuncionales: '职能角色',
      newRolFuncional: '新建职能角色',
      editRolFuncional: '编辑职能角色',
      descripcion: '描述',
      nivelMinimo: '最低级别',
      nivelMaximo: '最高级别',
      puestosAsociados: '职位',
      confirmDeactivateRol: '停用此职能角色？',
      rolFuncional: '职能角色',
      direccion: '地址',
      codigoPostal: '邮政编码',
      provincia: '省份',
      pais: '国家',
      telefono: '电话',
      personaContacto: '联系人',
      latitud: '纬度',
      longitud: '经度',
      urlUbicacion: '位置链接（Google Maps）',
      estado: '状态',
      editNivel: '编辑级别',
      colorFondo: '背景颜色',
      conflictoIndependencia: '独立性冲突',
      sinConflictos: '无冲突',
      puestoAsignado: '已分配职位',
      personaEnPuesto: '在岗人员',
      sinPuestoAsignado: '未分配职位',
      rolPlataforma: '平台角色',
      gestionarAcceso: '管理访问权限',
      usuarioVinculado: '关联用户',
      sinUsuarioVinculado: '未关联用户',
      personaPanel: {
        sinUsuario: '该人员未关联任何用户。',
        usuario: {
          email: '邮箱',
          rol: '角色',
          estado: '状态',
        },
      },
      tiposContrato: {
        label: '合同类型',
        title: '合同类型',
        newItem: '新增合同类型',
        editItem: '编辑合同类型',
        columns: {
          codigo: '代码',
          nombre: '名称',
          descripcion: '描述',
          activo: '启用',
          acciones: '操作',
        },
        placeholders: {
          nombre: '例如：固定期限',
          descripcion: '可选描述',
        },
        confirmDeactivate: '停用此合同类型？',
        enUso: '无法停用：有使用此类型的活动人员',
        itemLabel: '合同类型',
      },
      categoriasLaborales: {
        label: '职级类别',
        title: '职级类别',
        newItem: '新增职级',
        editItem: '编辑职级',
        columns: {
          codigo: '代码',
          nombre: '名称',
          descripcion: '描述',
          activo: '启用',
          acciones: '操作',
        },
        placeholders: {
          nombre: '例如：技术员',
          descripcion: '可选描述',
        },
        confirmDeactivate: '停用此职级？',
        enUso: '无法停用：有使用此职级的活动人员',
        itemLabel: '职级类别',
      },
      participacionEnPadre: '% 持股比例',
      arbolSocietario: '公司架构',
      vistaArbol: '树形',
      vistaTabla: '表格',
      tipoPersonaEmpleado: '员工',
      tipoPersonaColaborador: '合作者',
      tipoPersonaExterno: '外部人员',
      estadoActivo: '在职',
      estadoBaja: '离职',
      estadoSuspendido: '暂停',
      tipoCostoOperaciones: '运营',
      tipoCostoEstructuraProyecto: '项目结构',
      tipoCostoEstructuraCorporativa: '企业结构',
      rolesGobernanza: {
        title: '治理角色',
        newRol: '新角色',
        search: '搜索...',
        noResults: '无结果',
        colNombre: '自定义名称',
        colActivo: '启用',
        colAccion: '操作',
        saved: '保存成功',
        countLabel: '个角色',
        sistema: '系统',
        verFichaFG: '查看 FG 档案',
        verFichaTitle: '查看治理角色档案',
        vacante: '空缺',
        activo: '启用',
        inactivo: '停用',
        rolSistema: '系统角色',
        desactivar: '停用',
        activar: '启用',
        rolActivado: '角色已启用',
        rolDesactivado: '角色已停用',
        errorCambiarEstado: '更改状态时出错',
        colPuestoAsignado: '指定职位',
        colPersonaEnPuesto: '任职人员',
        tipoRol: {
          REPRESENTANTE_PROCESO: '流程代表',
          DIRECCION_RESPONSABLE_PROCESO: '流程责任方向',
          DPO: '数据保护官',
          OFICIAL_CUMPLIMIENTO: '合规官',
          PROPIETARIO_RIESGO: '风险责任人',
          AUDITOR_INTERNO: '内部审计员',
          RESPONSABLE_CALIDAD: '质量负责人',
          PROPIETARIO_PROCESO: '流程所有者',
          MIEMBRO_CONSEJO: '董事会成员',
          SECRETARIO_CONSEJO: '董事会秘书',
          PERSONALIZADO: '自定义',
        },
        modal: {
          tituloNuevo: '新增治理角色',
          tituloEditar: '编辑治理角色',
          tipoRol: '角色类型',
          puestoAsignado: '指定职位',
          seleccionar: '选择...',
          nombrePersonalizado: '自定义名称',
          placeholderNombre: '仅自定义类型时填写',
          ambito: '范围',
          placeholderAmbito: '流程范围...',
          cancelar: '取消',
          guardando: '保存中...',
          guardar: '保存',
          errorGuardar: '保存错误',
          camposObligatorios: '角色类型和职位为必填项',
        },
        // 0469: 平铺列表附加键
        mostrarInactivos: '显示停用',
        toastActivado: '角色已启用',
        toastDesactivado: '角色已停用',
        confirmDesactivar: '是否停用此治理角色？',
        toggleActivoError: '无法更改状态',
        verAuditoria: '查看审计',
        colConflicto: '冲突',
        acciones: '操作',
        editar: '编辑',
        eliminar: '删除',
        sinConflictos: '无冲突',
        conflictoIndependencia: '独立性冲突',
        sinPuestoAsignado: '未指定职位',
        ambito: '范围',
        de: '共',
        roles: '个角色',
      },
      // 0469: 统一的治理角色面板 — 3 个选项卡 数据 | FG 档案 | 审计
      rolGobernanzaPanel: {
        nuevoRol: '新增治理角色',
        tabDatos: '数据',
        tabFicha: 'FG 档案',
        tabAuditoria: '审计',
        fichaDeshabilitada: '此类型的角色没有专属 FG 档案',
        auditoriaDeshabilitada: '保存角色后可用',
        tipoRol: '角色类型',
        puesto: '指定职位',
        ambito: '范围',
        ambitoHelp: '角色的适用范围',
        nombrePersonalizado: '自定义名称',
        nombrePersonalizadoSoloPersonalizado: '仅当角色类型为自定义时可用',
        nombrePersonalizadoPlaceholder: '例如：IT 风险负责人',
        activo: '启用',
        seleccionar: '选择...',
        personaEnPuesto: '任职人员',
        rolSistemaNoEditable: '系统角色 — 类型、职位和启用状态不可编辑',
        errorTipoObligatorio: '角色类型必填',
        errorPuestoObligatorio: '职位必填',
        cancelar: '取消',
        guardar: '保存',
        saving: '保存中...',
        fichaNoExiste: '此角色尚无 FG 档案。',
        crearFicha: '创建治理角色档案',
        creandoFicha: '创建中...',
        fichaCreada: '档案已创建',
        fichaGuardada: '档案已保存',
        fichaMision: '角色使命',
        fichaFunciones: '职能与责任',
        fichaCompetencias: '所需能力',
        fichaRelaciones: '关键关系',
        fichaIndicadores: '绩效指标',
        errorCargarFicha: '加载档案时出错',
        errorCargarAuditoria: '加载审计时出错',
        auditoriaVacia: '此角色尚无审计记录。',
        auditoriaLimiteNota: '显示最近 50 条。完整记录请从合规模块查看。',
        auditoriaAccionCrear: '已创建',
        auditoriaAccionActualizar: '已更新',
        auditoriaAccionEliminar: '已删除',
        auditoriaUsuarioDesconocido: '未知用户',
        auditoriaDatosAnteriores: '原数据',
        auditoriaDatosNuevos: '新数据',
      },
      // 0470: 新的治理角色类型目录（按租户）
      tiposRolGobernanza: {
        titulo: '治理角色',
        buscar: '搜索...',
        mostrarInactivos: '显示停用',
        nuevoCustom: '新增自定义角色',
        nuevoCustomTitulo: '新建自定义治理角色',
        nombre: '名称',
        descripcion: '描述',
        cancelar: '取消',
        crear: '创建',
        nombreRequerido: '名称必填',
        tipoCreado: '角色类型已创建',
        tipoEliminado: '类型已删除',
        sistemaNoDesactivable: '系统类型不可停用',
        confirmEnUso: '该类型有活跃指派。请先重新分配或停用。',
        confirmEliminar: '删除此角色类型？',
        badgeSistema: '系统',
        ambitoGlobal: '全局',
        ambitoPorProceso: '按流程',
        colNombre: '名称',
        colDescripcion: '描述',
        colAmbito: '范围',
        colPuestos: '职位',
        colActivo: '启用',
        colFicha: 'FG 档案',
        colAcciones: '操作',
        estadoActivo: '启用',
        estadoInactivo: '停用',
        fichaVer: '查看 FG',
        fichaCrear: '创建 FG',
        editar: '编辑',
        eliminar: '删除',
        sinResultados: '无结果',
        // 0473 — 与 RF 统一（底部计数器 + 切换动作）
        contadorRoles: '角色',
        toggleActivar: '启用',
        toggleDesactivar: '停用',
      },
      // 0470: 治理角色类型面板重设计（4 个选项卡）
      tipoRolGobernanzaPanel: {
        tabDatos: '数据',
        tabAsignaciones: '指派',
        tabFicha: 'FG 档案',
        tabAuditoria: '审计',
        asignacionesDeshabilitada: '请先保存类型',
        fichaDeshabilitada: '请先保存类型',
        auditoriaDeshabilitada: '请先保存类型',
        nuevoTipo: '新角色类型',
        badgeSistema: '系统',
        codigo: '代码',
        nombre: '名称',
        descripcion: '描述',
        ambito: '范围',
        ambitoGlobal: '全局',
        ambitoPorProceso: '按流程（从流程档案管理）',
        activo: '启用',
        avisoSistema: '这是系统角色类型：名称不可更改，指派从流程档案管理。',
        sistemaSiempreActivo: '（系统类型不可停用）',
        guardar: '保存',
        creacionDesdeCatalogo: '新类型从目录创建',
        porProcesoInfo: '按流程的角色从每个流程的档案管理。',
        seleccionarPuesto: '选择职位',
        asignar: '指派职位',
        confirmDesasignar: '移除此指派？',
        asignacionesActivas: '活跃指派',
        sinAsignaciones: '无活跃指派',
        vacante: '空缺',
        quitar: '移除',
        errorCargar: '无法加载详情',
      },
      // 0453: 占用状态（1 职位 = 最多 1 个活跃指派）
      estadoVacante: '空缺',
      estadoOcupado: '已占用',
      paginationPrev: '上一页',
      paginationNext: '下一页',
      paginationPage: '页',
      // 统一职位面板 (0453): 5 个选项卡 数据 | 指派 | 组织架构 | 档案 | 治理角色
      puestoPanel: {
        nuevoPuesto: '新建职位',
        tabDatos: '数据',
        tabAsignaciones: '指派',
        tabOrganigrama: '组织架构',
        tabFicha: '档案',
        tabRolesGobernanza: '治理角色',
        // 0470 M9: 与治理角色选项卡的互通
        rolesGobernanzaInfo: '此职位的治理角色。按流程的角色（代表、负责方向）从流程档案管理。',
        seleccionarRol: '选择角色类型',
        asignarRol: '指派角色',
        rolesActivos: '活跃角色',
        sinRolesGobernanza: '未指派治理角色',
        badgeSistema: '系统',
        gestionadoPorProceso: '从流程管理',
        quitar: '移除',
        confirmDesasignarRol: '从该职位移除此角色？',
        sistema: '系统',
        labelTitulo: '标题',
        labelCodigo: '代码',
        labelNivel: '级别',
        labelRolFuncional: '职能角色',
        labelOrganizacion: '组织',
        labelDepartamento: '部门',
        codigoPreview: '（预览）',
        codigoAutogenerado: '（自动生成）',
        codigoPendiente: '（保存时生成）',
        requiredTitulo: '标题为必填项',
        requiredNivel: '级别为必填项',
        requiredOrg: '组织为必填项',
        save: '保存',
        saving: '保存中...',
        cancel: '取消',
        createPuesto: '创建职位',
        asignacionesHeader: '职位指派',
        asignarPersona: '指派人员',
        tooltipOcupado: '请先结束之前的指派',
        sinAsignaciones: '无指派',
        colPersona: '人员',
        colCodigoPersona: '代码',
        colFechaInicio: '开始日期',
        colEstado: '状态',
        colAccion: '操作',
        estadoActivo: '活跃',
        estadoFinalizado: '已结束',
        finalizar: '结束',
        asignacionFinalizada: '指派已结束',
        errorFinalizar: '结束指派时出错',
        asignacionCreada: '人员指派成功',
        asignarPersonaA: '指派人员至',
        buscarPersona: '搜索人员',
        placeholderBuscar: '姓名或代码...',
        fechaInicio: '开始日期',
        fte: '全职当量 (0-1)',
        esPrincipal: '主要',
        asignar: '指派',
        asignando: '指派中...',
        seleccionaPersona: '请选择一名人员',
        reportaA: '汇报给',
        sinPadre: '无上级',
        supervisa: '管辖',
        sinHijos: '无下属职位',
        editarOrganigrama: '在组织架构中编辑',
        fichaPuesto: '职位档案',
        fichaNoExiste: '此职位尚无档案。',
        crearFicha: '创建职位档案',
        creandoFicha: '创建中...',
        guardarFicha: '保存档案',
        fichaCreada: '档案创建成功',
        fichaGuardada: '档案已保存',
        errorCargarFicha: '加载档案出错',
        fichaMision: '职位使命',
        fichaFunciones: '职责与责任',
        fichaCompetenciasTecnicas: '技术能力',
        fichaCompetenciasTransversales: '通用能力',
        fichaFormacion: '所需学历',
        fichaExperiencia: '所需专业经验',
        fichaCertificaciones: '必备证书',
        fichaCondiciones: '职位条件',
        // 0454 M2 · UX
        asignarPersonaTooltipOcupado: '先结束当前活动的任命, 再指派其他人员.',
        dirtyWarningTitle: '未保存的更改',
        dirtyWarningMessage: '您在 {tabs} 中有未保存的更改, 要放弃吗?',
        dirtyWarningDiscard: '放弃并关闭',
        dirtyWarningCancel: '返回面板',
        guardarOrganigrama: '保存组织结构',
        cancelarOrganigrama: '取消',
        guardarFichaTab: '保存档案',
        cancelarFicha: '取消',
        // 0454 M3 · 组织架构可编辑
        supervisor: '主管',
        sinPadreSeleccionado: '未指定上级',
        placeholderBuscarPadre: '搜索上级职位...',
        // 0454 M4 · Tiptap 工具栏
        toolbarHeading1: '标题 1',
        toolbarHeading2: '标题 2',
        toolbarHeading3: '标题 3',
        toolbarBold: '粗体',
        toolbarItalic: '斜体',
        toolbarUnderline: '下划线',
        toolbarBulletList: '项目列表',
        toolbarOrderedList: '编号列表',
        toolbarBlockquote: '引用',
        toolbarAlignLeft: '左对齐',
        toolbarAlignCenter: '居中',
        toolbarAlignRight: '右对齐',
        toolbarColor: '颜色',
        toolbarColorBlack: '黑色',
        toolbarColorBlue: '蓝色',
        toolbarColorRed: '红色',
        toolbarColorGreen: '绿色',
        toolbarClearFormat: '清除格式',
        toolbarUndo: '撤销',
        toolbarRedo: '重做',
      },
    },
    madurez: {
      codigo: '编号',
      cuestionarioLecturaSolo: '诊断已批准 · 问卷已冻结（只读）',
      fuenteAnalisis: '分析',
      fuente_CUESTIONARIO: '问卷',
      fuente_REAL: '实际',
      fuente_AMBAS: '两者',
      niveles: '等级 — 将鼠标悬停在单元格上查看详情',
      resumenEjecutivo: '执行摘要',
      dafo: 'SWOT 矩阵',
      lecturaConsultor: '顾问解读',
      borradorIa: 'AI 草稿',
      requiereRevision: '需要复核',
      conclusionBloqueada: '诊断已批准 · 只读',
      pick_QUICK_WIN: '速赢',
      pick_RETO: '挑战',
      pick_POSIBLE: '可能',
      pick_DESCARTAR: '舍弃',
      escenarios: '假设情景',
      nuevoEscenario: '创建情景',
      sinEscenarios: '暂无情景。创建一个以模拟结构性变更。',
      viable: '可行',
      noViable: '不可行',
      impulsoras: '推动力',
      restrictivas: '阻力',
      evaluarEscenario: '评估',
      generarDesde: '以此为基础生成',
      lectura: '视图',
      vistaReal: '实际（组织结构图）',
      vistaCuestionario: '问卷',
      nivelGlobalReal: '全局等级 · 实际',
      nivelGlobalCuest: '全局等级 · 问卷',
      coberturaCuest: '问卷',
      opcional: '（可选）',
      errMotorNoDisponible: 'AI 引擎不可用或额度不足。请重试或检查服务提供方。',
      errRespuestaInvalida: 'AI 返回了无效的响应。请重试。',
      errGenerico: '发生错误。请重试。',
      conclusionConsultor: '治理工程师结论',
      conclusionPlaceholder: '你对情境的专业解读，超越客观数据……',
      hojaRuta: '路线图',
      responsable: '负责人',
      horizonte: '时间范围',
      discrepanciaRealCuest: '现实 ↔ 问卷差异',
      colDimension: '维度',
      colReal: '现实',
      colPercepcion: '感知',
      colLectura: '解读',
      lecturaSinDato: '无问卷数据',
      lecturaCoincide: '一致',
      lecturaInfravalora: '被低估：现实高于感知',
      lecturaSobreestima: '被高估：感知比实际更成熟',
      guardarConclusion: '保存结论',
      guardado: '已保存',
      generando: '正在生成分析……',
      regenerar: '重新生成 AI 分析',
      generarAnalisis: '生成 AI 分析',
      via: 'V·IA',
      cargando: '加载中……',
      sinAnalisis: '尚无 AI 分析。生成一份以获取诊断与建议。',
      sentinel: 'Sentinel',
      sentinelError: '严重警报——在解决之前无法验证该分析。',
      sentinelWarn: '需复核的警告。',
      diagnostico: '诊断',
      recomendaciones: '建议',
      noAnclada: 'AI 提议',
      anclada: '已关联到目录',
      fortalezas: '优势',
      riesgos: '风险',
      validar: '验证分析',
      bloqueadoSentinel: '被 Sentinel 阻止',
      generandoInforme: '正在生成报告……',
      descargarInforme: '下载 Word 报告',
      descargarPy: '下载 .py 导出脚本',
      generandoPy: '正在生成 .py……',
      generarMegaprompt: '生成 claude.ai 超级提示词',
      generandoMegaprompt: '正在生成超级提示词……',
      verInformeHtml: '生成 HTML 报告',
      generandoHtml: '正在生成 HTML……',
    },
  },

  // People module
  people: {
    title: '人员',
    directory: '人员目录',
    newPerson: '+ 新建人员',
    total: '总计',
    employees: '员工',
    collaborators: '合作者',
    externals: '外部人员',
    active: '在职',
    inactive: '离职',
    noPosition: '无岗位',
    filteredStats: '已筛选统计',
    searchPlaceholder: '按姓名、姓氏或邮箱搜索...',
    allTypes: '所有类型',
    employee: '员工',
    collaborator: '合作者',
    external: '外部人员',
    all: '全部',
    activeFilter: '在职',
    inactiveFilter: '离职',
    allDepts: '所有部门',
    fullName: '全名',
    position: '职位',
    type: '类型',
    loadingDirectory: '加载目录中...',
    noPersonsFound: '未找到符合筛选条件的人员。',
    previous: '上一页',
    pageOf: (page: number, total: number) => `第 ${page} 页，共 ${total} 页`,
    next: '下一页',
    // Create person modal
    newPersonTitle: '新建人员',
    code: '编号',
    personType: '人员类型',
    firstName: '名',
    lastName: '姓',
    email: '邮箱',
    phone: '电话',
    hireDate: '入职日期',
    contractType: '合同类型',
    directionCode: '方向编号',
    requiredFields: '编号、名、姓和邮箱为必填项',
    errorCreatePerson: '创建人员时出错',
    creatingPerson: '创建中...',
    createPerson: '创建人员',
    assetsPlaceholder: '分配给此人的资产通过V·IA库存模块管理。',
    goToInventory: '前往库存',
    form: {
      code: '编号 *',
      personType: '人员类型 *',
      firstName: '名 *',
      lastName: '姓 *',
      email: '邮箱 *',
      phone: '电话',
      hireDate: '入职日期',
      contractType: '合同类型',
      directionCode: '部门编号',
      nif: '税号',
      displayName: '显示名称',
      secondaryEmail: '备用邮箱',
      mobilePhone: '手机号码',
      status: '状态',
      jobCategory: '职位类别',
      terminationDate: '离职日期',
      birthDate: '出生日期',
      supervisor: '主管',
      country: '国家',
      gender: '性别',
      readOnly: '（只读）',
      codigoAutogenerado: '（自动生成）',
      paisDerivadoTooltip: '来源于主要工作中心',
      department: '部门',
      tipoCosto: '成本类型',
      centroCostes: '成本中心（项目）',
      workCenter: '工作中心',
      center: '工作中心',
      cambioEstadoTitulo: (de: string, a: string) => `状态变更：${de} → ${a}`,
      cambioEstadoMotivo: '原因',
      cambioEstadoFecha: '变更日期',
      cambioEstadoObs: '备注',
      cambioEstadoObsPlaceholder: '可选',
      cambioEstadoConfirmar: '确认变更',
      cambioEstadoMotivoBaja: '此状态变更需要填写原因',
    },
    placeholders: {
      code: 'EMP-XXX',
      contractType: '长期',
    },
    validation: {
      required: '必填字段',
      emailFormat: '邮箱格式无效',
      codeRequired: '编码为必填项',
      nameRequired: '姓名为必填项',
      lastNameRequired: '姓氏为必填项',
      emailRequired: '邮箱为必填项',
    },
    pageSize: '每页',
    showing: (from: number, to: number, total: number) => `${from}-${to} 共 ${total}`,
    selectAll: '全选',
    selected: (n: number) => `已选 ${n} 项`,
    bulkTerminate: (n: number) => `停用 (${n})`,
    bulkExportCsv: (n: number) => `导出CSV (${n})`,
    confirmBulkTerminate: (n: number) => `确定停用 ${n} 人？这将关闭所有活跃的分配。`,
    bulkTerminateSuccess: (n: number) => `${n} 人已成功停用`,
    bulkTerminateError: '停用人员时出错',
    // Detail page
    loadingPerson: '加载人员中...',
    personNotFound: '未找到人员',
    terminate: '终止',
    confirmTerminate: '终止此人员？所有活跃分配将被关闭。',
    confirmFinalizarAsignacion: '结束此分配？数据将保留在历史记录中。',
    confirmDesasignarProyecto: '取消此人员的项目分配？',
    confirmDesasignarCentro: '取消此人员的中心分配？',
    // Detail tabs
    personalData: '个人数据',
    positionAndChart: '岗位与组织架构',
    projects: '项目',
    centers: '工作中心',
    assets: '资产',
    governanceTab: '治理',
    processesAndDocs: '流程与文档',
    processesRepresented: '代表的流程',
    noProcesses: '未分配流程',
    documentsCreated: '创建的文档',
    documentsApproved: '批准的文档',
    noDocuments: '无文档',
    noGovernanceRoles: '未分配治理角色',
    rolesByPosition: '按职位的角色',
    directRoles: '直接角色',
    // Data tab
    savedOk: '保存成功',
    saveChanges: '保存更改',
    // Position tab
    currentPosition: '当前岗位',
    noPositionAssigned: '无岗位分配',
    orgChartPosition: '组织架构位置',
    positionHistory: '岗位历史',
    noHistory: '无历史',
    puesto: '岗位',
    department: '部门',
    from: '从',
    to: '至',
    status: '状态',
    activeStatus: '活跃',
    finishedStatus: '已结束',
    finalize: '结束',
    confirmFinalize: '结束此分配？',
    // Projects tab
    assignedProjects: '已分配项目',
    noProjects: '无已分配项目',
    project: '项目',
    role: '角色',
    dedication: '投入',
    unassign: '取消分配',
    confirmUnassignProject: '从此项目取消分配？',
    // Centers tab
    workCenters: '工作中心',
    noCenters: '无已分配中心',
    center: '中心',
    city: '城市',
    primary: '主要',
    yes: '是',
    no: '否',
    confirmUnassignCenter: '从此中心取消分配？',
    // Assets tab
    assignedAssets: '已分配资产',
    noAssets: '无已分配资产（资产从V·IA资产管理）',
    asset: '资产',
    // Governance tab
    governanceRoles: '治理角色',
    noGovernanceRolesLegacy: '无治理角色分配（通过岗位分配）',
    scope: '范围',
    processOwner: '流程所有者',
    dpo: '数据保护官',
    complianceOfficer: '合规官',
    internalAuditor: '内部审计员',
    boardMember: '董事会成员',
    personaDrawer: {
      title: '人员详情',
      contact: '联系方式',
      positionInfo: '职位与部门',
      organization: '组织',
      miniOrgChart: '迷你组织架构',
      supervisor: '直属上级',
      subordinates: '直属下属',
      main: '主要',
    },
    historialEstado: {
      titulo: '状态历史',
      sinCambios: '无已记录的变更。',
      colFecha: '日期',
      colDe: '从',
      colA: '至',
      colMotivo: '原因',
      colObservaciones: '备注',
      colPor: '操作人',
    },
  },

  // Home page
  home: {
    searchPlaceholder: '搜索文档、流程、人员...',
    documents: '文档',
    processes: '流程',
    pending: '待处理',
    current: '有效',
    engineerings: '治理工程',
    recentActivity: '最近活动',
    noRecentActivity: '无最近活动',
    news: '新闻',
    pendingDocs: '待处理',
    created: '已创建',
    updated: '已更新',
    deleted: '已删除',
    sent: '已发送',
    approved: '已批准',
    rejected: '已驳回',
    published: '已发布',
  },

  // Quick search
  search: {
    searching: '搜索中...',
    noResultsFound: '未找到结果',
    recentDocs: '最近文档',
    typeToSearch: '输入以按名称或编号搜索文档',
    searchTooltip: '搜索 (Ctrl+K)',
  },

  // Breadcrumb
  breadcrumb: {
    home: '首页',
    detail: '详情',
    admin: '管理',
    aiAssistant: 'AI助手',
    orgChart: '组织架构图',
    organizations: '组织管理',
    governance: '治理',
    people: '人员',
    processMap: '流程地图',
    characterization: '特征化',
    smartSearch: '智能搜索',
    docAudit: '文档审计',
    dashboards: '仪表板',
    regulations: '规范',
    internalAudits: '内部审计',
    nonConformities: '不合格项',
    projects: '项目',
    purchases: '采购',
    assets: '资产',
    requests: '请求',
    news: '新闻',
    surveys: '调查',
    events: '活动',
    settings: '设置',
    import: '导入',
    assistant: '助手',
    document: '文档',
    process: '流程',
    processSheet: '流程表',
  },

  // Settings page
  settings: {
    loadingConfig: '加载配置中...',
    brandTab: '品牌标识',
    generalTab: '常规设置',
    usersTab: '用户',
    statsTab: '统计',
    configSaved: '配置保存成功',
    logoUpdated: '标志已更新',
    errorUpload: '上传文件时出错',
    // Brand
    brand: '品牌',
    brandName: '品牌名称',
    font: '字体',
    colors: '颜色',
    primary: '主色',
    secondary: '次色',
    accent: '强调色',
    preview: '预览',
    logos: '标志',
    mainLogo: '主标志',
    headerLogo: '页头标志',
    favicon: '网站图标',
    saving: '保存中...',
    saveChanges: '保存更改',
    // General config
    generalConfig: '常规设置',
    language: '语言',
    spanish: '西班牙语',
    timezone: '时区',
    customDomain: '自定义域名',
    plan: '计划',
    activeModules: '活跃模块',
    // Users
    users: '用户',
    newUser: '+ 新建用户',
    name: '姓名',
    emailCol: '邮箱',
    roleCol: '角色',
    statusCol: '状态',
    actionsCol: '操作',
    activeUser: '活跃',
    inactiveUser: '停用',
    deactivate: '停用',
    confirmDeactivate: '此用户将被停用。是否继续？',
    userDeactivated: '用户已停用',
    newUserTitle: '新建用户',
    fullName: '全名',
    emailAndNameRequired: '邮箱和姓名为必填项',
    userCreated: '用户创建成功',
    creatingUser: '创建中...',
    createUser: '创建用户',
    // Stats
    tenantStats: '租户统计',
    totalUsers: '总用户数',
    activeUsers: '活跃用户',
    totalDocs: '总文档数',
    publishedDocs: '已发布文档',
    totalProcesses: '总流程数',
    loadingStats: '加载统计中...',
  },

  // Audit page (compliance)
  audit: {
    title: '文档审计',
    allEntities: '所有实体',
    allActions: '所有操作',
    fromDate: '从',
    toDate: '至',
    clearFilters: '清除筛选',
    refresh: '刷新',
    date: '日期',
    user: '用户',
    action: '操作',
    entity: '实体',
    detail: '详情',
    loadingRecords: '加载记录中...',
    noRecordsFound: '未找到审计记录',
    pageOf: (page: number, total: number, records: number) =>
      `第 ${page} 页，共 ${total} 页 · ${records} 条记录`,
    previous: '上一页',
    next: '下一页',
    errorLoading: '加载审计记录时出错',
    actions: {
      createDoc: '创建文档',
      editDoc: '编辑文档',
      sendReview: '提交审核',
      approveVersion: '批准版本',
      rejectVersion: '拒绝版本',
      publishDoc: '发布文档',
      deactivate: '停用',
      createRef: '创建引用',
      deleteRef: '删除引用',
      startChat: '开始对话',
      login: '登录',
      logout: '登出',
    },
  },

  // Compliance module
  compliance: {
    tabs: {
      normative: '规范',
      internalAudit: '内部审计',
      nonConformities: '不符合项',
    },
    landing: {
      title: '合规管理',
      description: '法规、审计、不符合项和纠正措施。确保符合企业标准。',
      normativeDesc: '企业法规框架管理和合规管理',
      auditDesc: '内部系统审计的计划和监控',
      nonConformitiesDesc: '不符合项和纠正措施的记录和处理',
    },
  },

  // Process module
  process: {
    madurezEmpresa: {
      tab: '企业成熟度',
      errorEvals: '无法加载评估',
      errorHeatmap: '无法加载热力图',
      errorCrear: '无法创建评估（是否有编辑权限？）',
      errorSembrar: '无法播种能力项',
      errorConclusion: '无法保存结论',
      errorAprobar: '无法批准（是否有主管角色？）',
      titulo: '企业成熟度 · PEMM（转型能力）',
      nueva: '+ 新建评估',
      global: '企业级别',
      vacio: '尚无转型能力评估。使用"+ 新建评估"创建第一个。',
      sinSiembra: '该评估尚无能力项。先在 E-1 播种全部 13 项以开始审计。',
      sembrar: '播种 13 项能力',
      heatmap: '能力矩阵 13 × 4',
      aprobar: '批准评估',
      aprobada: '✓ 已批准（只读）',
      verDetalle: '在问卷中高亮显示',
      eslabon: '最薄弱环节：决定企业级别',
      conNota: '有备注',
      radar: '按类别雷达图（最薄弱环节）',
      evolucion: '企业演变',
      sinEvolucion: '只有一次评估时尚无可显示的演变。',
      conclusion: '治理工程师结论',
      conclusionPh: '摘要：企业能否支撑其流程的持续改进？最薄弱环节、解锁下一级别的要素……',
      guardando: '保存中…',
      guardar: '保存',
      guardado: '✓ 已保存',
      cuestionarioTitulo: 'PEMM-企业问卷',
      errorGuardar: '无法保存（是否有编辑权限？）',
      anadirNota: '添加备注',
      lecturaSolo: '评估已批准 · 问卷已冻结（只读）',
      cuestionarioAyuda:
        '将每条陈述从 1（完全没有）到 4（完全符合）评分。能力项的平均值决定其 E 级别；企业的水平取决于其最薄弱环节。结果会立即反映在热力图和雷达图上。',
      expandirBloques: '展开区块',
      contraerBloques: '收起区块',
      expandirNotas: '展开备注',
      contraerNotas: '收起备注',
      refNiveles: '级别参考（Hammer）',
      iaErrorCargar: '无法加载分析',
      iaErrorGenerar: '无法生成分析（是否有编辑权限或配额？）',
      iaErrorValidar: '无法核验（是否有主管角色或 Sentinel 警报？）',
      iaErrorExport: '无法生成报告（是否有编辑权限？）',
      iaTitulo: 'V·IA Governance 分析',
      iaGenerando: '生成中…',
      iaRegenerar: '重新生成 AI 分析',
      iaGenerar: '生成 V·IA 分析',
      iaValidado: '✓ 已核验',
      iaGenerado: '已生成',
      expWordTip: '企业版 Word 报告（PO.34.MO.02）',
      expWord: '下载 Word 报告',
      expPyTip: '用于在 claude.ai 重现 Word 的 .py 导出器（PO.34.HE.02）',
      expPy: '下载 .py 导出器',
      expMegaTip: '用于在 claude.ai 生成高级报告的 Megaprompt .md',
      expMega: '生成 megaprompt claude.ai',
      expHtmlTip: '独立 HTML 报告（在新标签页打开）',
      expHtml: '生成 HTML 报告',
      iaVacio:
        '尚无分析。生成一个：V·IA 将读取 13 项能力，诊断最薄弱环节、解锁下一级别的要素以及 SWOT。',
      iaSentinel: '⚠ Sentinel 标记了严重警报：核验前请检查。',
      iaResumen: '执行摘要',
      iaLecturaConsultor: '顾问解读',
      iaDafo: 'SWOT 矩阵',
      iaDiagnostico: '诊断',
      iaRecomendaciones: '建议',
      iaFortalezas: '优势',
      iaRiesgos: '风险',
      iaValidando: '核验中…',
      iaValidar: '核验分析',
      iaBorrador: 'AI 草稿',
      tcoError: '无法保存研究（编辑权限？）',
      tcoTitulo: '投资研究 · TCO / ROI',
      tcoTotal: '总拥有成本 (TCO)',
      roiHorizonte: '周期投资回报率',
      roiPermanente: '稳态投资回报率',
      payback: '回本期',
      noRecuperado: '未回本',
      flujoNeto: '净现金流',
      flujoAcum: '累计现金流',
      horizonte: '周期（年）',
      costesDirectos: '直接成本（€）',
      costesIndirectos: '间接成本（€）',
      curvaAdopcion: '采用曲线（0-1）',
      beneficios: '年度收益（稳态）',
      conceptoPh: '概念（例如：减少返工）',
      addBeneficio: '+ 添加收益',
      tcoGuardar: '保存研究',
      tcoNota: '周期投资回报率 = (Σ 收益 − TCO) / TCO × 100。年度收益 = 满额收益 × 采用曲线。',
    },
    madurezPemm: {
      tab: '流程成熟度',
      titulo: '流程成熟度 · PEMM（Hammer）',
      proceso: '流程',
      elegirProceso: '— 选择一个流程 —',
      evaluacion: '评估',
      nueva: '+ 新建评估',
      global: '流程级别',
      vacioProceso: '选择一个流程以评估其 PEMM 成熟度（Hammer 的 13 项使能要素）。',
      vacioEval: '该流程尚无 PEMM 评估。使用“+ 新建评估”创建第一个。',
      sinSiembra: '该评估尚无使能要素。先在 P-1 播种全部 13 项以开始审计。',
      sembrar: '播种 13 项使能要素（从流程地图预填充）',
      heatmap: '使能要素矩阵 13 × 4',
      aprobar: '批准评估',
      aprobada: '✓ 已批准（只读）',
      eslabon: '最薄弱环节：决定流程级别',
      errorProcesos: '无法加载流程',
      errorEvals: '无法加载评估',
      errorHeatmap: '无法加载热力图',
      errorCrear: '无法创建评估（是否有编辑权限？）',
      errorSembrar: '无法播种使能要素',
      errorGuardar: '无法保存级别（是否有编辑权限？）',
      errorAprobar: '无法批准（是否有主管角色？）',
      verDetalle: '查看定义和备注',
      conNota: '有备注',
      cuestionarioTitulo: 'PEMM 问卷',
      cuestionarioAyuda:
        '将每条陈述从 1（完全没有）到 4（完全符合）评分。使能因素的平均值决定其 P 级别；流程的水平取决于其最薄弱环节。结果会立即反映在热力图和雷达图上。',
      addNota: '+ 备注',
      notaVer: '📝 备注',
      refNiveles: '级别参考（Hammer）',
      expandirBloques: '展开区块',
      contraerBloques: '收起区块',
      expandirNotas: '展开备注',
      contraerNotas: '收起备注',
      anadirNota: '添加备注',
      lecturaSolo: '评估已批准 · 问卷已冻结（只读）',
      radar: '按类别雷达图（最薄弱环节）',
      evolucion: '流程演变',
      sinEvolucion: '只有一次评估时尚无可显示的演变。',
      conclusion: '治理工程师结论',
      conclusionPh: '诊断摘要：最薄弱环节、解锁下一级别的要素、优先事项……',
      guardando: '保存中…',
      guardar: '保存',
      guardado: '✓ 已保存',
      metodoTitulo: '如何评估 · PEMM（Hammer 2007）',
      metodoTexto:
        '这是一种专家审计：对每个使能要素，选择流程真正达到的 P 级别。流程级别 = 其最薄弱环节（最小值）。提升最薄弱的使能要素即可提升流程。',
      detalle: '使能要素详情',
      detalleVacio: '在矩阵中选择一个使能要素，查看其各级别定义并添加备注。',
      notaIngeniero: '工程师备注（证据）',
      notaPh: '支持所分配级别的证据……',
      guardarNota: '保存备注',
      errorNota: '无法保存备注',
      errorConclusion: '无法保存结论',
      iaTitulo: 'V·IA Governance 分析',
      iaResumen: '执行摘要',
      iaBorrador: 'AI 草稿',
      iaLecturaConsultor: '顾问解读',
      zenicTitulo: '审计证据（ZENIC）',
      zenicCualitativa: '定性回答（访谈）',
      zenicCuantitativa: '定量回答（数据、%）',
      zenicAnalisis: '级别理由（为何是该 P）',
      zenicLeccion: '经验教训',
      guardarZenic: '保存证据',
      roadmapTitulo: '路线图',
      matrizPick: 'PICK 优先级（影响 × 复杂度）',
      kotter: 'Kotter',
      brecha: '差距',
      owner: '负责人',
      crucesTitulo: '交叉策略（SWOT）',
      cruceFo: 'SO · 优势 + 机会（进攻）',
      cruceFa: 'ST · 优势 + 威胁（防御）',
      cruceDo: 'WO · 劣势 + 机会（调整）',
      cruceDa: 'WT · 劣势 + 威胁（生存）',
      iaGenerar: '生成 V·IA 分析',
      iaRegenerar: '重新生成',
      iaGenerando: '生成中…',
      iaGenerado: '已生成',
      iaValidado: '✓ 已核验',
      iaVacio:
        '尚无分析。生成一个：V·IA 将读取 13 项使能要素，诊断最薄弱环节、解锁下一级别的要素以及 SWOT。',
      iaDiagnostico: '诊断',
      iaRecomendaciones: '建议（按优先级）',
      iaFortalezas: '优势',
      iaRiesgos: '风险',
      iaDafo: 'SWOT 矩阵',
      iaDafoF: '优势',
      iaDafoD: '劣势',
      iaDafoO: '机会',
      iaDafoA: '威胁',
      iaValidar: '核验分析',
      iaValidando: '核验中…',
      iaValidadoNota: '✓ 分析已核验（可用于报告）',
      iaSentinel: '⚠ Sentinel 标记了严重警报：核验前请检查。',
      iaErrorCargar: '无法加载分析',
      iaErrorGenerar: '无法生成分析（是否有编辑权限或配额？）',
      iaErrorValidar: '无法核验（是否有主管角色或 Sentinel 警报？）',
      expMega: '生成 megaprompt claude.ai',
      expMegaTip: '用于在 claude.ai 生成高级报告的 Megaprompt .md',
      expHtml: '生成 HTML 报告',
      expHtmlTip: '独立 HTML 报告（在新标签页打开）',
      iaErrorExport: '无法生成报告（是否有编辑权限？）',
      expWord: '下载 Word 报告',
      expWordTip: '企业版 Word 报告（PO.34.MO.02）',
      expPy: '下载 .py 导出器',
      expPyTip: '用于在 claude.ai 重现 Word 的 .py 导出器（PO.34.HE.02）',
      tcoTitulo: '投资研究 · TCO / ROI',
      tcoTotal: '总拥有成本 (TCO)',
      roiHorizonte: '周期投资回报率',
      roiPermanente: '稳态投资回报率',
      payback: '回本期',
      noRecuperado: '未回本',
      flujoNeto: '净现金流',
      flujoAcum: '累计现金流',
      horizonte: '周期（年）',
      costesDirectos: '直接成本（€）',
      costesIndirectos: '间接成本（€）',
      curvaAdopcion: '采用曲线（0-1）',
      beneficios: '年度收益（稳态）',
      conceptoPh: '概念（例如：减少返工）',
      addBeneficio: '+ 添加收益',
      tcoGuardar: '保存研究',
      tcoError: '无法保存研究（编辑权限？）',
      tcoNota: '周期投资回报率 = (Σ 收益 − TCO) / TCO × 100。年度收益 = 满额收益 × 采用曲线。',
    },
    madurez: {
      title: '流程成熟度 · BPMM',
      loading: '正在加载流程成熟度…',
      nuevaEvaluacion: '新建评估',
      aprobar: '批准',
      cuestionario: '问卷',
      ocultarCuestionario: '隐藏问卷',
      cuestionarioTitle: 'BPMM 问卷',
      cuestionarioLoading: '正在加载 BPMM 问卷…',
      nivelGlobalReal: '整体等级 · 实际（流程图）',
      nivelGlobalCuest: '整体等级 · 问卷',
      coberturaModelo: '模型覆盖率',
      coberturaCuest: '问卷：',
      estado: '状态',
      codigo: '编码：',
      emptyTitle: '尚无流程成熟度评估',
      emptyDesc: '创建评估：引擎将读取流程图并生成 7 个 BPMM 支柱。',
      lectura: '读取方式：',
      vistaReal: '实际（流程图）',
      vistaCuestionario: '问卷',
      heatmapTitle: '热力图 7 支柱 × 5 等级',
      radarTitle: '雷达图 7 支柱',
      cuestIntro: '请根据你的感受作答（1 = 从不，5 = 总是）。结果将与流程图的实际读取进行对照。',
      liderazgo: '领导力',
      anadirNota: '添加备注',
      guardando: '正在保存…',
      guardarRespuestas: '保存答案',
      respuestasGuardadas: '答案已保存',
      lecturaSolo: '评估已批准 · 问卷已冻结（仅供查看）',
      tab: '成熟度',
      analisisIA: 'V·IA Governance 分析',
      ocultarAnalisis: '隐藏 AI 分析',
      discrepancias: '问卷差异',
      discrepanciasEmpty: '尚未记录任何答案。',
      discrepante: '差异（方差 ≥ 阈值）',
      colPregunta: '问题',
      colRespondentes: '答题数',
      colVarianza: '方差',
      timeline: '整体等级演变',
      timelineEmpty: '历史数据尚不足。',
      niveles: 'BPMM 等级（Robledo）— 将鼠标悬停在单元格上查看详情',
      notasPilar: '治理备注',
      expandirBloques: '展开区块',
      contraerBloques: '折叠区块',
      expandirNotas: '展开备注',
      contraerNotas: '折叠备注',
      conclusionConsultor: '治理工程师结论',
      opcional: '（可选）',
      guardarConclusion: '保存结论',
      conclusionBloqueada: '诊断已批准 · 仅供查看',
      conclusionPlaceholder: '你对情境的专业解读，超越客观数据……',
      guardado: '已保存',
      cuestionarioHint: '选择一项评估以作答，并将客观数据与主观感受进行三角验证。',
      analisis: {
        fuente: '来源：',
        ambas: '两者',
        real: '实际',
        cuestionario: '问卷',
        generando: '正在生成分析…',
        regenerar: '重新生成 AI 分析',
        generar: '生成 AI 分析',
        generarAnalisis: '生成 AI 分析',
        validar: '验证分析',
        cargando: '加载中…',
        vacio:
          '尚无分析。生成一个：AI 将诊断流程成熟度并提出按优先级排序的改进措施（AI 提出建议，顾问进行验证）。',
        sinAnalisis:
          '尚无 AI 分析。生成一个：AI 将诊断流程成熟度并提出按优先级排序的改进措施（AI 提出建议，顾问进行验证）。',
        basadoNotas: '该分析纳入了顾问的治理备注。',
        recomendaciones: '建议',
        fortalezas: '优势',
        riesgos: '风险',
        dafoFortalezas: '优势',
        dafoDebilidades: '劣势',
        dafoOportunidades: '机会',
        dafoAmenazas: '威胁',
        analisisIA: 'V·IA Governance 分析',
        descargarInforme: '下载 Word 报告',
        descargarPy: '下载 .py 导出器',
        generarMegaprompt: '生成 claude.ai 超级提示词',
        verInformeHtml: '生成 HTML 报告',
        resumenEjecutivo: '执行摘要',
        validado: '已验证',
        borradorIa: 'AI 草稿',
        lecturaConsultor: '顾问解读',
        dafo: 'SWOT 矩阵',
        diagnostico: '诊断',
        anclada: '已锚定至目录',
        hojaRuta: '路线图',
        responsable: '负责人',
        horizonte: '时间范围',
        discrepancias: '问卷差异',
        discrepanciasEmpty: '尚未记录任何答案。',
        colPregunta: '问题',
        colRespondentes: '答题数',
        colVarianza: '方差',
        discrepante: '差异（方差 ≥ 阈值）',
        fuenteAnalisis: '分析',
        fuente_CUESTIONARIO: '问卷',
        fuente_REAL: '实际',
        fuente_AMBAS: '两者',
        fuenteHelp_CUESTIONARIO: '团队感受 / 工程师判断',
        fuenteHelp_REAL: '流程图的客观读取',
        fuenteHelp_AMBAS: '比较实际与感受并突出差距',
        via: 'V·IA',
      },
    },
    mapaTitle: '流程图',
    explorerTitle: '文档浏览器',
    tooltips: {
      mapa: {
        titulo: '流程图',
        ayuda:
          '管理系统所有流程的全局视图。\n\n· 战略流程 (PE) — 定义方向和公司治理。\n· 运营流程 (PO) — 直接创造业务价值。\n· 支持流程 (PS) — 为运营流程提供服务。\n\n点击组别查看其流程，点击流程查看其流程图和相关文档。',
      },
      explorador: {
        titulo: '文档浏览器',
        ayuda:
          '管理系统完整的文档目录。\n\n· 只有处于现行状态的文档才是强制性的。\n· 使用筛选器按类型、流程或状态搜索。\n· 点击文档查看其完整内容和交叉引用。',
      },
      ficha: {
        titulo: '流程说明书',
        ayuda:
          '流程的主要文档，记录其目的、范围、负责人、输入、输出和指标。\n\n· 流程负责人确保其执行和持续改进。\n· 输入和输出定义了流程与其他系统流程的边界。',
      },
    },
    procesos: '流程',
    expandir: '展开',
    colapsar: '折叠',
    cargando: '加载中...',
    seleccionarProceso: '选择一个流程',
    fichaAqui: '流程特性表将在此处显示',
    seleccionarDocumento: '选择一个文档',
    deLaSeccion: '来自文档部分',
    ficha: '特性表',
    docs: '文档',
    flow: '流程图',
    visor: '查看器',
    flujograma: '流程图',
    fichaCaracterizacion: '流程特性表',
    cargandoFicha: '加载流程特性表...',
    procesoNoEncontrado: '未找到流程',
    vigente: '已发布',
    exportarPdf: '导出PDF',
    editarProximamente: '编辑（即将推出）',
    responsable: '负责人',
    asistente: 'AI助手',
    fichaSubtitle: '流程特性表',
    aiDisponible: 'AI助手即将推出',
    escribirMensaje: '输入消息...',
    identificacion: '1. 标识',
    codigo: '编号',
    nombreProceso: '流程名称',
    tipologia: '类型',
    grupoFuncional: '功能组',
    fase: '阶段',
    involucrados: '参与者',
    sinRolesGobernanza: '未分配治理角色',
    descripcionSeccion: '2. 描述',
    sinDescripcion: '未登记描述',
    alcance: '范围',
    alcanceNoDefinido: '当前数据模型中未定义范围',
    entradasSalidas: '3. 输入和输出',
    entradas: '输入',
    salidas: '输出',
    sinEntradas: '无数据 — 当前版本中未建模输入',
    sinSalidas: '无数据 — 当前版本中未建模输出',
    reportesRequisitos: '4. 报告和要求',
    reportes: '报告',
    requisitos: '要求',
    sinReportes: '无关联报告',
    requisitosNoModelados: '当前版本中未建模法规要求',
    documentacion: '5. 文档',
    sinDocumentos: '无关联文档',
    herramientasSeccion: '6. 工具',
    sinHerramientas: '无关联工具',
    indicadoresSeccion: '7. 指标',
    sinIndicadores: '未定义流程指标',
    objetivo: '目标',
    calculo: '计算',
    documentos: '文档',
    cargandoDocumentos: '加载文档中...',
    seleccioneProcesoFicha: '选择一个流程以查看其特性表',
    ingenieria: '工程',
    origen: '来源',
    destino: '目标',
    anadir: '添加',
    pendiente: '待处理',
    crearDocumento: '创建文档',
    creando: '创建中...',
    nombreDocumento: '文档名称',
    tipoDocumental: '文档类型',
    anadirALista: '添加到列表',
    anadirDocumento: '添加文档',
    anadirIndicador: '添加指标',
    nuevoIndicador: '新指标',
    frecuencia: '频率',
    valorActual: '当前值',
    sinDescripcionCorto: '无描述',
    descripcionPlaceholder: '流程描述...',
    alcancePlaceholder: '流程范围...',
    direccionResponsable: '负责方向',
    representanteProceso: '流程代表',
    nuevoInvolucrado: '新参与者：',
    textoEntrada: '条目文本：',
    nuevoReporte: '新报告：',
    nuevoRequisito: '新要求：',
    nuevaHerramienta: '新工具：',
    eliminarBorrador: '删除草稿',
    exportPdfPendiente: 'PDF导出 — 功能待开发。',
  },

  // Document viewer
  documentViewer: {
    cargandoDocumento: '加载文档中...',
    documentoNoEncontrado: '未找到文档',
    proceso: '流程',
    directorResponsable: '负责总监',
    representante: '代表',
    primeraAprobacion: '首次批准',
    versionVigente: '当前版本',
    motivoRechazo: '驳回原因（可选）',
    indicarMotivo: '请说明驳回原因...',
    confirmarRechazo: '确认驳回',
    procesando: '处理中...',
    ocultarIndice: '隐藏目录',
    mostrarIndice: '显示目录',
    indice: '目录',
    exportando: '导出中...',
    aprobado: '文档已成功批准',
    rechazado: '文档已驳回 — 退回审核',
    publicado: '文档已作为当前版本发布',
    enviadoAprobacion: '文档已提交审批',
    devueltoBorrador: '文档已退回草稿',
    transicionEjecutada: '转换执行成功',
  },

  // Document explorer panel
  docExplorer: {
    documentos: '文档',
    todos: '全部',
    estrategicos: 'PE — 战略',
    operativos: 'PO — 运营',
    soporte: 'PS — 支持',
    buscar: '搜索...',
    buscarPlaceholder: '搜索文档...',
    cargandoDocumentos: '加载文档中...',
    noEncontrados: '未找到文档',
    codigo: '编号',
    nombre: '名称',
    proceso: '流程',
    procesoCol: '流程',
    representante: '代表',
    direccion: '方向',
    tipo: '类型',
    estado: '状态',
    edicion: '版本',
    fecha: '日期',
    totalDocumentos: '文档总数',
    vigentes: '已发布',
    borrador: '草稿',
    enRevision: '审核中',
    aprobado: '已批准',
    todosEstados: '所有状态',
    todosProcesos: '所有流程',
    mostrando: '显示',
    de: '共',
  },

  // Explorer workspace
  workspace: {
    exploradorDocumentos: '文档浏览器',
    mapaProcesos: '流程地图',
    cargandoProcesos: '加载流程中...',
    sinGrupo: '无分组',
    pendientes: '待处理',
    enRevision: '审核中',
    enAprobacion: '审批中',
    fichaCaracterizacion: '流程特性表',
    documentos: '文档',
    selectFromSheet: '从特性表中选择文档',
    ficha: '特性表',
    docs: '文档',
    flow: '流程图',
    visor: '查看器',
    tooltips: {
      showFullSheet: '显示完整特性表',
      showOnlyDocs: '仅显示文档',
      hideSheet: '隐藏特性表',
      expandDocument: '展开文档',
      openFullViewer: '在完整查看器中打开',
    },
  },

  // Changelog modal
  changelog: {
    nuevaEdicion: '新版本',
    describaCambios: '描述促使此新版本的更改',
    descripcionCambios: '变更描述',
    obligatorio: '创建新版本需要提供变更描述。',
    crearEdicion: '创建版本',
    creando: '创建中...',
  },

  // Metadata tab
  metadata: {
    informacionDocumento: '文档信息',
    codigo: '编号',
    nombre: '名称',
    tipoDocumental: '文档类型',
    proceso: '流程',
    estado: '状态',
    edicionActual: '当前版本',
    empresa: '公司',
    direccion: '方向',
    fechaCreacion: '创建日期',
    ultimaActualizacion: '最后更新',
    seleccionarProceso: '— 选择流程 —',
    errorAlGuardar: '保存时出错',
    cicloVida: '生命周期',
  },

  // TOC sidebar
  toc: {
    buscarSeccion: '搜索章节...',
    portada: '封面',
  },

  // Version diff
  diff: {
    comparacionVersiones: '版本比较',
    cerrar: '✕ 关闭',
    anadidasLabel: '新增',
    eliminadasLabel: '删除',
    sinCambios: '无变化',
    sinContenido: '无内容可比较',
    lineaAnadida: '新增行',
    lineaEliminada: '删除行',
  },

  // Viewer header
  viewerHeader: {
    guardando: '保存中...',
    autoguardadoActivo: '自动保存已激活',
    guardar: '保存',
    guardarYCerrar: '保存并关闭',
    descartarCambios: '放弃更改',
    editar: '编辑',
    nuevaEdicion: '新版本',
    creando: '创建中...',
    exportarDocx: '导出DOCX',
    exportando: '导出中...',
    documentoExportado: '文档导出成功',
    errorAlExportar: '导出时出错',
    confirmarDescartar: '放弃更改',
    perderanCambios: '自上次保存以来的所有更改将丢失。此操作无法撤销。',
    descartar: '放弃',
  },

  // Flow editor
  flow: {
    cargandoFlujograma: '加载流程图中...',
    reintentar: '重试',
    guardarBorrador: '保存草稿',
    enviarARevision: '提交审核',
    historialVersiones: '版本历史',
    deshacer: '撤销 (Ctrl+Z)',
    rehacer: '重做 (Ctrl+Y)',
    acercar: '放大',
    alejar: '缩小',
    ajustarPantalla: '适应屏幕',
    colapsarSubprocesos: '折叠子流程',
    mostrarCuadricula: '显示/隐藏网格',
    mostrarMinimapa: '显示/隐藏小地图',
    exportarPng: '导出PNG',
    exportarPdf: '导出PDF',
    guardando: '保存中...',
    viaAsistente: 'VIA — 流程图助手',
    viaDescription: '流程图设计AI助手即将推出。',
    entendido: '知道了',
    excedeTamano: (max: number) =>
      `流程图超过规范大小（最多${max}个节点）。请考虑将活动分组为子流程。`,
    cerrarAviso: '关闭提示',
    sinFlujogramaPublicado: '此流程没有已发布的流程图',
    crearDesdeEditor: '从流程图编辑器创建',
    vistaLectura: '流程图 — 只读视图',
    abrirEnEditor: '在编辑器中打开',
    guardarAntes: '请在提交审核前保存流程图。',
    enviarRevisionConfirm: '提交流程图审核？审核期间无法编辑。',
    publish: '发布',
    publishSuccess: '流程图发布成功',
    nuevaEdicionCreada: '新版本已创建',
    errorNuevaEdicion: '创建新版本时出错',
    errorEliminar: '删除草稿时出错',
    errorArchivar: '归档时出错',
    nuevaSolicitud: '新申请',
    solicitudesVinculadas: '关联申请',
    // Toolbar — alignment & distribution
    alinearIzquierda: '左对齐',
    alinearCentroH: '水平居中对齐',
    alinearDerecha: '右对齐',
    alinearArriba: '顶部对齐',
    alinearCentroV: '垂直居中对齐',
    alinearAbajo: '底部对齐',
    distribuirH: '水平分布',
    distribuirV: '垂直分布',
    igualarTamano: '统一大小',
    autoLayout: '自动布局',
    autoLayoutTitle: '自动布局 (ELK)',
    calculando: '计算中...',
    exportar: '导出',
    cuadricula: '网格',
    minimapa: '小地图',
    toggleRedimensionar: '切换调整大小',
    versiones: '版本',
    auditTrail: '审计跟踪',
    audit: '审计',
    // Header actions
    guardar: '保存',
    guardarYCerrar: '保存并关闭',
    descartar: '放弃',
    editar: '编辑',
    nuevaEdicionLabel: '新版本',
    eliminar: '删除',
    archivar: '归档',
    pantallaCompleta: '全屏',
    salirPantallaCompleta: '退出全屏',
    publicarConFirma: '使用电子签名发布',
    // Properties panel
    conector: '连接器',
    etiqueta: '标签',
    etiquetaConector: '连接器标签',
    estiloLinea: '线条样式',
    solida: '实线',
    discontinua: '虚线',
    punteada: '点线',
    tipoFlecha: '箭头类型',
    flecha: '箭头',
    sinFlecha: '无箭头',
    bidireccional: '双向',
    abierta: '开放',
    colorLabel: '颜色',
    propiedades: '属性',
    sinNombre: '未命名',
    responsable: '负责人',
    sinAsignar: '未分配',
    descripcionLabel: '描述',
    faseLabel: '阶段',
    nombre: '名称',
    cargandoPuestos: '加载职位中...',
    responsableId: '负责人 (ID)',
    idPuesto: '职位ID',
    documentosVinculados: '关联文档',
    documentoReferencia: '参考文档',
    condicion: '条件',
    tamano: '大小',
    procesoReferencia: '参考流程',
    cargandoProcesos: '加载流程中...',
    procesoIdLabel: '流程ID',
    idProceso: '流程ID',
    nodoTipoInfo: (tipo: string) => `${tipo}节点 — 仅支持标签。`,
    subprocesoInfo: '子流程内容通过在画布上展开来编辑。',
    sinResultadosPara: (term: string) => `没有"${term}"的结果`,
    sinDocumentosDisponibles: '此流程没有可用文档。',
    cargandoDocumentos: '加载文档中...',
    buscarDocumento: '搜索文档...',
    eliminarDoc: '删除',
    // Node type labels
    tipoActividad: '活动',
    tipoDecision: '决策',
    tipoDocumento: '文档',
    tipoSubproceso: '子流程',
    tipoInicio: '开始',
    tipoFin: '结束',
    tipoProcesoRef: '流程（引用）',
    tipoProcesoGobierno: '治理机构',
    tipoResultado: '结果',
    tipoProcesoRefCorto: '流程引用',
    tipoProcesoGobiernoCorto: '治理机构',
    // Phases fallback
    faseDiagnostico: '诊断',
    faseArquitectura: '架构',
    faseDespliegue: '部署',
    faseOperacion: '运营',
    faseMadurez: '成熟',
    // Shape library
    formas: '形状',
    fases: '阶段',
    agregarFase: '+ 添加阶段',
    nombrePlaceholder: '名称...',
    arrastrarForma: '将形状拖到画布上添加。',
    clicRenombrar: '点击重命名',
    renombrarFase: '重命名阶段',
    eliminarFase: '删除阶段',
    expandirIzquierda: '向左扩展',
    expandirDerecha: '向右扩展',
    gobCorporativo: '公司治理',
    procesoRef: '流程（引用）',
    // Node detail panel
    cerrar: '关闭',
    herramientas: '工具',
    etiquetasLabel: '标签',
    documentoReferenciado: '引用文档',
    subprocesoLabel: '子流程',
    nodosInternos: (n: number) => `${n}个内部节点`,
    procesoVinculado: '关联流程',
    si: '是',
    no: '否',
  },

  // Process map
  processMap: {
    title: '流程地图',
    kpi: {
      procesosDocumentados: '已记录流程',
      flujogramasPublicados: '已发布流程图',
      documentosVigentes: '现行文档',
      auditoriasPendientes: '待审计',
      conformidad: '合规率',
      esteTrimestre: (n: number) => `本季度+${n}`,
      delTotal: (pct: number) => `总量的${pct}%`,
      sinCambios: '无变化',
      vsMesAnterior: (n: number) => `较上月-${n}`,
      vsUltimoCiclo: (pct: number) => `较上周期+${pct}%`,
      procesosEnRevision: '审核中的流程',
      pendientesAprobacion: '待批准',
      indicadoresCumplidos: '已达标指标',
      vsObjetivoAnual: '+5% 较年度目标',
    },
    cargandoMapa: '加载流程地图中...',
    errorAlCargar: '加载出错',
    sinProcesosRegistrados: '未找到已注册的流程',
    noticias: '新闻',
    verTodo: '查看全部',
    noticiasRecientes: (n: number) => `${n} 条近期新闻`,
    verTodas: '查看全部',
    procesosEstrategicos: 'PE — 战略流程',
    ingenieriasGobernanza: 'PO — 治理工程',
    procesosSoporte: 'PS — 支持流程',
    cargandoGrupo: '加载流程组中...',
    grupoNoEncontrado: '未找到组',
    volverAlMapa: '← 返回流程地图',
    sinProcesosGrupo: '该组中没有已注册的流程',
    gestionDocumental: '文档管理',
    documentoVinculado: (n: number) => `${n} 个关联文档`,
    procedimientos: '程序文件',
    fichasCaracterizacion: '流程特性表',
    flujogramas: '流程图',
    instruccionesTecnicas: '技术指导书',
    procesosDelGrupo: '组内流程',
    explorador: '文档浏览器',
    cargandoProceso: '加载流程中...',
    procesoNoEncontrado: '未找到流程',
    mapa: '地图',
    secciones: '章节',
    descripcion: '描述',
    responsables: '负责人',
    involucrados: '参与者',
    documentacion: '文档',
    requisitosLabel: '要求',
    reportesLabel: '报告',
    herramientasLabel: '工具',
    indicadoresLabel: '指标',
    formacion: '培训',
    sinDescripcion: '未登记描述。',
    tipologiaLabel: '类型',
    grupo: '组',
    responsableLabel: '负责人',
    sinResponsable: '未分配负责人',
    sinDocumentacion: '无关联文档',
    seccionesDelProceso: '流程章节',
    vigenteBadge: '生效中',
    desdePill: '自',
    actPill: '更新',
    volverAlFlujograma: '返回流程图',
    sinInvolucrados: '无已注册参与者',
    sinDocumentosVinculados: '无关联文档',
    sinRequisitos: '未定义要求',
    sinReportes2: '未定义报告',
    sinHerramientasDefinidas: '未定义工具',
    sinIndicadoresDefinidos: '未定义指标',
    seccionEnDesarrollo: '章节开发中。',
    formacionEnDesarrollo: '培训章节正在开发中。与流程相关的培训计划在资源模块中配置。',
    objetoYAlcance: '目标和范围',
    direccionYRepresentante: '方向和代表',
    rolesYParticipantes: '角色和参与者',
    normasAplicables: '适用标准',
    trimestralCMI: '季度BSC',
    onboardingAnual: '年度入职',
    kpisDefinidos: (n: number) => `${n} 个已定义KPI`,
    estrategico: '战略',
    operativo: '运营',
    soporteCorto: '支持',
    mapaDeProcesos: '流程地图',
    gestionDocumentalTitulo: '文档管理',
    gestionDocDepartamental: '部门文档管理',
    gestionDocDepartamentalDesc: '按部门和功能区域管理文档生命周期。',
    gestionDocProyectos: '项目文档管理',
    gestionDocProyectosDesc: '与特定项目和交付物相关的文档管理。',
    equipoDelGrupo: '小组团队',
    directorResponsableLabel: '负责总监',
    dirGralLabel: 'CEO — 总方向',
    representanteDelProceso: '流程代表',
    pendienteDeAsignar: '待分配',
    actividadReciente: '近期活动',
    kpiProcesosDelGrupo: '小组流程',
    kpiDocumentosVinculados: '关联文档',
    kpiConFaseAsignada: '已分配阶段',
    kpiConResponsable: '有负责人',
    kpiFasesCubiertas: '已覆盖阶段',
    kpiDocsPorProceso: '每流程文档数',
    kpiConformidad: '合规率',
    sinProcesos: '无流程',
    sinFasesAsignadas: '无已分配阶段',
    mayoriaEn: (fase: string) => `多数在${fase}`,
    delGrupo: '组内',
    deProcesos: (n: number) => `${n} 个流程中`,
    delCicloDeVida: '生命周期',
    promedioDelGrupo: '组平均',
    pendienteDeMedicion: '待测量',
    disponible: '可用',
    pendienteDeConfigurar: '待配置',
    servicioInformation: 'VALOR·IA Information 服务',
    actividad: {
      fichaActualizada: '特征表已更新',
      flujogramaEnviado: '流程图已提交审批',
      nuevoDocVinculado: '新文档已链接到流程',
      indicadorRevisado: '绩效指标已审查',
      procesoAsignado: '流程已分配给新代表',
      hace2Dias: '2天前',
      hace3Dias: '3天前',
      hace5Dias: '5天前',
      hace1Semana: '1周前',
      hace2Semanas: '2周前',
    },
    vistaLectura: '流程图 — 只读视图',
    abrirEnEditor: '在编辑器中打开',
    exportarPdf: '导出PDF',
    highlightCards: {
      procesosActivos: '活跃流程',
      procesosActivosDesc: '运行中的流程总数',
      documentosVigentes: '现行文档',
      documentosVigentesDesc: '已发布状态的文档',
      pendientesRevision: '待审核',
      pendientesRevisionDesc: '审核队列中的文档',
      indicadoresAlDia: '指标更新',
      indicadoresAlDiaDesc: '已更新测量的KPI',
    },
    newsPlaceholder: {
      gobernanza: '治理',
      procesos: '流程',
      cumplimiento: '合规',
      tecnologia: '技术',
      noticia1Titulo: '董事会批准新的公司治理框架',
      noticia1Extracto: '董事会已批准新的治理框架，将于下季度生效。',
      noticia2Titulo: '流程工程诊断阶段完成',
      noticia2Extracto: '已识别47个关键运营流程并记录其相互关系。',
      noticia3Titulo: 'ISO 9001内部审计：结果积极',
      noticia3Extracto: '内部审计确认与标准要求的符合率为94%。',
      noticia4Titulo: 'V·IANet 2.0迁移成功完成',
      noticia4Extracto: '平台所有模块已更新至新架构。',
    },
  },

  // Import page (batch)
  importPage: {
    titulo: '导入文档',
    paso1: '步骤1：配置来源',
    paso1Desc: '指定包含要导入的DOCX文件的目录路径。系统将递归扫描所有子目录。',
    rutaDirectorio: '目录路径',
    escanear: '扫描',
    escaneando: '扫描中...',
    formatoEsperado: '预期文件格式：',
    paso2: '步骤2：预览',
    cambiarRuta: '更改路径',
    listo: '就绪',
    yaExiste: '已存在',
    noImportable: '不可导入',
    creado: '已创建',
    actualizado: '已更新',
    omitido: '已跳过',
    configurarOrigen: '配置来源',
    vistaPrevia: '预览',
    importandoLabel: '导入中',
    resultadosLabel: '结果',
    importandoDocumentos: '导入文档中...',
    procesandoArchivos: (n: number) => `正在处理 ${n} 个DOCX文件。这可能需要一些时间。`,
    errorImportacion: '导入过程中出错：',
    paso4: '步骤4：导入结果',
    procesoFinalizado: '导入过程已完成',
    nuevaImportacion: '新导入',
    irExplorador: '前往浏览器',
    totalArchivos: '文件总数',
    listosImportar: '准备导入',
    yaExistentes: '已存在',
    noImportables: '不可导入',
    totalProcesados: '已处理总数',
    creados: '已创建',
    omitidos: '已跳过',
    errores: '错误',
    errorEscanear: '扫描目录时出错',
    errorImportar: '导入文档时出错',
    archivo: '文件',
    codigoCol: '编号',
    tipoCol: '类型',
    procesoCol: '流程',
    tituloCol: '标题',
    estadoCol: '状态',
    nombreCol: '名称',
    detalleCol: '详情',
  },

  // Tech discipline
  information: {
    tabs: {
      search: '智能搜索',
      audit: '文档审计',
      dashboards: '管理仪表板',
    },
  },
  reputation: {
    tabs: {
      news: '新闻',
      surveys: '调查',
      events: '活动',
    },
  },
  tech: {
    title: '数字化发展',
    description: 'V·IANet平台、API、集成和自动化。管理生态系统的技术工具。',
    descriptions: {
      apps: '平台集成管理应用',
      projects: '项目管理和计划跟踪',
      procurement: '采购和供应管理',
      assets: '库存和技术资产管理',
      requests: '内部请求和申请管理',
      ocr: '扫描实体文件和PDF进行数字化',
    },
    tabs: {
      apps: 'Apps',
      projects: '项目',
      procurement: '采购',
      assets: '资产',
      requests: '请求',
      ocr: 'OCR / 数字化',
    },
    ocr: {
      title: 'OCR / 数字化',
      dropzone: '拖拽文件或点击选择',
      proceso: '流程',
      tipo: '文档类型',
      processing: 'OCR处理中...',
      submit: '数字化',
    },
  },

  // Sign — M15
  sign: {
    title: '签名配置 (M15)',
    estado: '签名状态',
    activo: 'M15签名已激活。可在管理 > 平台 > 模块中管理激活状态。',
    inactivo: 'M15签名未激活。请在管理 > 平台 > 模块中启用。',
    nivelDefault: '默认签名级别',
    horasRecordatorio: '提醒时间（小时）',
    horasEscalacion: '升级时间（小时）',
    caInterna: '内部CA已启用',
    firmaInvitacion: '邀请签名',
    politicaEntidad: '按实体类型的最低签名级别',
    certificados: '证书',
    sinCertificados: '未配置证书',
    qtspTitle: 'QTSP 提供商（合格签名）',
    proximoQTSP: 'QTSP集成将在未来版本中提供',
    actionCenter: {
      title: '操作中心',
      tabs: {
        pendientes: '待处理',
        historial: '历史记录',
        delegaciones: '委托',
      },
      pendientesCount: '待处理',
      ordenar: '排序',
      porFecha: '日期',
      porTipo: '类型',
      cargando: '加载中...',
      cargandoMas: '加载中...',
      cargarMas: '加载更多',
      irARevisar: '去审核',
      verEntidad: '查看实体',
      delegar: '委托',
      delegadoIdLabel: '被委托人ID (UUID)',
      motivoLabel: '原因',
      confirmarDelegacion: '确认',
      delegacionesRecibidas: '收到的委托',
      delegacionesRealizadas: '发出的委托',
      delegadaPor: '委托人',
      delegadaA: '委托给',
      activa: '进行中',
      finalizada: '已完成',
      // 0662: 区分旧版（签字）与工作流委托 + 撤销
      tipoLegacyBadge: '签字',
      tipoWorkflowBadge: '工作流',
      revocar: '撤销',
      confirmarRevocar: '确定要撤销该委托吗？被委托人将无法再看到待处理项。',
      timelineTitle: '工作流程',
      timelineSolicitante: '申请人',
      timelinePublicador: '发布者',
      timelineDone: '已完成',
      timelinePending: '待处理',
      timelineFuture: '未来',
      timelineNotFound: '没有工作流信息',
      timelineRevisor: '审核人',
      timelineAprobador: '批准人',
      timelineNotificar: '通知',
      timelineNotifyDelivered: '已发送',
      timelineNotifyPending: '发送中',
      detail: {
        tipoEntidad: '实体类型',
        idArtefacto: '工件 ID',
        estado: '状态',
        estadoPendiente: '待批准',
        nivelFirma: '签名等级',
        solicitante: '申请人',
        fechaLimite: '截止日期',
        hash: '哈希',
        decision: '决定',
        recibido: '已接收',
        accionAyuda:
          '点击"批准"确认转换或"拒绝"并提供原因。"前往审核"打开工件以便在决定前进行检查。',
        accionAyudaTitulo: '操作',
      },
      tabEnCurso: '进行中',
      enCursoEmpty: '您没有进行中的申请',
      enCursoTooltipSolicitante: '您发起了此申请',
      enCursoTooltipParticipante: '您参与了之前的阶段',
      enCursoTooltipFuturo: '您将在未来的阶段中处理',
      // 0666b: Action Center Premium UX
      premium: {
        title: '行动中心',
        searchPlaceholder: '按主题、范围、代码或标题搜索…',
        emptyTitle: '收件箱为空',
        emptySubtitle: '您目前没有待处理的申请。',
        loading: '正在加载申请…',
        errorTitle: '无法加载',
        errorRetry: '重试',
        countItems: '{n} 项申请',
        tabAll: '全部',
        tipoOrganigrama: '组织架构图',
        tipoDoc: '文档',
        tipoFlujograma: '流程图',
        tipoDocLegacy: '旧版文档',
        tipoPeticion: '请求',
        tipoCompra: '采购',
        actionOpen: '打开制品',
        actionOpenOrganigrama: '打开组织架构图',
        actionOpenDocumento: '打开文档',
        actionOpenFlujograma: '打开流程图',
        detailLoadError: '无法加载详情。请关闭弹窗后重试。',
        colTipo: '类型',
        colAsunto: '主题',
        colCodigo: '代码',
        colSolicitante: '申请人',
        colFecha: '日期',
        colEstado: '状态',
        detailDetails: '详情',
        detailFlow: '审批流程',
        detailNotified: '通知与附件',
        detailAsunto: '主题',
        detailAlcance: '范围',
        detailSolicitante: '申请人',
        detailFechaSolicitud: '申请日期',
        detailEstado: '状态',
        detailNotifiedTitle: '已通知',
        detailAttachmentsTitle: '附件',
        detailAttachmentsEmpty: '无附件',
        detailNotifiedEmpty: '未记录通知',
        observationsLabel: '备注',
        observationsPlaceholder: '添加可选备注…',
        rejectMotivoLabel: '拒绝原因',
        rejectMotivoPlaceholder: '说明拒绝原因…',
        actionCancel: '取消',
        actionReject: '拒绝',
        actionApprove: '批准',
        actionApproving: '正在批准…',
        actionRejecting: '正在拒绝…',
        approveError: '无法批准',
        rejectError: '无法拒绝',
        actionDelegate: '委托',
        // 0669 — 视图 + 分页 + 增强的高级模态
        vistaLabel: '视图',
        vistaPendientes: '待处理',
        vistaEnCurso: '进行中',
        vistaHistorico: '历史',
        paginacionAnterior: '上一页',
        paginacionSiguiente: '下一页',
        paginacionPagina: '第 {n} 页,共 {total} 页',
        paginacionPageSize: '每页',
        paginacionTotal: '{n} 个结果',
        flowFecha: '日期',
        flowRol: '角色',
        flowPuesto: '职位',
        flowMotivo: '评论',
        flowMotivoEmpty: '暂无评论',
        flowMotivoTitle: '查看评论',
        notifiedRol: '角色',
        notifiedPuesto: '职位',
        notifiedToggleShow: '显示通知人 ({n})',
        notifiedToggleHide: '隐藏通知人',
        delegar: {
          title: '委托审批',
          searchPlaceholder: '按姓名、邮箱或代码搜索人员…',
          searchHint: '输入以搜索租户中的人员。',
          loading: '正在加载人员…',
          noResults: '未找到匹配项',
          motivoLabel: '委托原因',
          motivoPlaceholder: '说明委托原因（至少 5 个字符）…',
          cancel: '取消',
          confirm: '委托',
          delegating: '正在委托…',
          removeSelection: '移除选择',
          errorNoPersona: '请从列表中选择一个人员。',
          errorPersonaSinUsuario: '此人员没有关联用户，无法接收委托。',
          errorMotivoCorto: '原因至少需要 5 个字符。',
          errorDelegar: '无法委托',
        },
        adjuntos: {
          title: '附件',
          dropzonePrompt: '将文件拖到此处或点击上传',
          dropzoneHint: 'PDF、Office、图片、ZIP — 每个文件最大 50 MB',
          subiendo: '正在上传…',
          listaVacia: '此工件尚无附件',
          descargar: '下载',
          eliminar: '删除附件',
          confirmarEliminar: '删除此附件？可从回收站恢复。',
          subidoPor: '上传者',
          tamano: '大小',
          fecha: '日期',
          errorMimeType: '不支持的文件类型',
          errorTamanoExcedido: '文件超过 50 MB',
          errorSubida: '无法上传文件',
          errorDescarga: '无法生成下载链接',
          errorEliminacion: '无法删除附件',
        },
        comentarios: {
          title: '评论',
          placeholder: '为此工件撰写评论…',
          enviar: '发送',
          enviando: '正在发送…',
          cargando: '正在加载评论…',
          listaVacia: '暂无评论。第一个补充背景说明吧。',
          eliminar: '删除评论',
          confirmarEliminar: '删除此评论？将被标记为已删除。',
          errorCrear: '无法发送评论',
          errorEliminar: '无法删除评论',
          tiempoAhora: '刚刚',
          tiempoMinutos: '{n} 分钟前',
          tiempoHoras: '{n} 小时前',
          tiempoDias: '{n} 天前',
          empty: '暂无评论',
        },
      },
    },
  },

  // Landing page common
  disciplines: {
    tooltips: {
      structure:
        'VALOR·IA Structure 管理组织架构：人员、职位、部门、实体和治理关系。\n\n它是"谁是谁"以及公司如何组织以执行其公司治理模式的数据库。',
      process:
        'VALOR·IA Process 管理流程的完整生命周期：特征描述、流程图、指标和规范文档。\n\n确保所有流程都根据治理模型进行文档化、分配和衡量。',
      information:
        'VALOR·IA Information 管理管理系统的文档语料库：标准、说明、记录和报告。\n\n确保信息是最新的、受控的，并且对所有参与流程的人员都是可访问的。',
      compliance:
        'VALOR·IA Compliance 管理合规性和审批工作流：审计、工作流和公司签署。\n\n保证对每一个影响公司治理的决定和变更都具有完整的可追溯性。',
      tech: 'VALOR·IA Tech 管理公司信息系统的技术资产、数字项目和工具。\n\n将技术架构与使用它的流程和人员联系起来。',
      reputation:
        'VALOR·IA Reputation 管理企业内部沟通：新闻、活动、调查和治理叙述。\n\n帮助组织的每一位成员了解并践行公司治理模式。',
    },
  },
  landing: {
    processes: '流程',
    documents: '文档',
    tabs: {
      summary: '概览',
      processes: '流程',
      documents: '文档',
      kpis: 'KPIs',
    },
    description: '描述',
    lifecycle: '生命周期',
    modules: '模块',
    lifecyclePhases: {
      diagnosis: '诊断',
      architecture: '架构',
      deployment: '部署',
      operation: '运营',
      maturity: '成熟度',
    },
    processesRegistered: (n: number, _mod?: string) => `此工程中已注册 ${n} 个流程。`,
    selectProcess: '从侧栏选择流程以查看其特性表。',
    documentsAssociated: (n: number, _mod?: string) => `已关联 ${n} 个规范文档。`,
    useExplorer: '使用文档浏览器浏览和管理文档。',
    kpisInDev: '指标面板开发中。',
  },

  // Archivos adjuntos
  archivos: {
    titulo: '附件',
    subir: '上传文件',
    enlace: '注册链接',
    sinArchivos: '无附件',
    eliminar: '删除文件',
    confirmarEliminar: '删除此文件？',
    subiendo: '上传中...',
    subido: '文件已上传',
    errorSubir: '上传错误',
    enlaceRegistrado: '链接已注册',
    archivo: '文件',
    enlaceExterno: '外部链接',
    storage: '存储',
    urlEnlace: '链接地址',
    nombreArchivo: '文件名',
    tipoMime: 'MIME类型',
    descripcion: '描述',
    tabArchivos: '文件',
    filtroTipoContenido: '内容类型',
    todosContenidos: '所有内容类型',
    digital: '数字',
    seleccionarArchivo: '选择文件',
    descargar: '下载',
    abrirEnlace: '打开链接',
    reemplazar: '替换文件',
  },

  // Migration dashboard
  migracion: {
    titulo: '迁移状态',
    progreso: '迁移进度',
    completados: '已完成',
    pendientes: '待处理',
    errores: '有错误',
    de: '共',
    documentosMigrados: '已迁移文档',
    cargando: '加载迁移状态中...',
    errorCargar: '加载迁移状态时出错',
    PENDIENTE: '待处理',
    COMPLETADO: '已完成',
    ERROR: '错误',
    oleada: '批次',
    documento: '文档',
    estado: '状态',
  },
  adminProcesos: {
    titulo: '流程管理',
    pestana: '管理',
    nuevoGrupo: '+ 新建组',
    nuevoProceso: '+ 新建流程',
    confirmarDesactivar: '停用流程？',
    confirmarDesactivarDesc: '将被标记为非活动状态。',
    avisoDocumentos: '关联文档。文档不会被删除。',
    desactivar: '停用',
    cancelar: '取消',
    inactivo: '非活动',
    renombrar: '重命名组',
    eliminarGrupo: '删除组',
    grupoConProcesos: '无法删除包含流程的组',
    grupoRenombrado: '组已重命名',
    procesoCreado: '流程已创建',
    procesoActualizado: '流程已更新',
    procesoDesactivado: '流程已停用',
    cargando: '加载流程中...',
    grupos: '功能组',
    procesos: '流程',
    nombreProceso: '流程名称...',
    nombreGrupo: '组名称...',
  },
  requests: {
    adminTitle: '请求管理',
    tooltips: {
      admin: {
        titulo: '请求管理',
        ayuda:
          '所有系统请求的管理面板。\n\n请求是结构化工作流，用于管理人员和部门之间的申请，具有完整的可追溯性和受控审批。',
      },
    },
    admin: {
      title: '请求配置',
      formularios: '表单',
      cadenas: '审批链',
    },
    nuevo: '新建',
    editar: '编辑',
    eliminar: '删除',
    confirmarEliminar: '删除此项？此操作无法撤消。',
    guardado: '保存成功',
    nombre: '名称',
    descripcion: '描述',
    codigo: '编码',
    orden: '排序',
    activo: '启用',
    grupo: '分组',
    tipo: '请求类型',
    proceso: '关联流程',
    requiereFirma: '需要签名',
    nivelFirma: '签名级别',
    fuenteResolucion: '解析来源',
    persona: '人员',
    importeMinimo: '最低金额',
    obligatorio: '必填',
    rolAprobacion: '审批角色',
    delegante: '委托人',
    delegado: '被委托人',
    fechaInicio: '开始日期',
    fechaFin: '结束日期',
    motivo: '原因',
    horasLimite: '限时（小时）',
    escaladoA: '升级至',
    nivelEscalado: '升级级别',
    evento: '事件',
    asunto: '主题',
    cuerpoHtml: 'HTML正文',
    sinDatos: '暂无配置数据',
    cargando: '加载中...',
    seleccionar: '选择...',
    todos: '全部',
    cancelar: '取消',
    // User-facing (solicitudes)
    nueva: '新申请',
    misSolicitudes: '我的申请',
    pendientesAprobar: '待审批',
    seleccionarTipo: '选择申请类型',
    cambiarTipo: '更改类型',
    guardarBorrador: '保存草稿',
    enviarAprobacion: '提交审批',
    aprobar: '批准',
    rechazar: '拒绝',
    devolver: '退回',
    confirmar: '确认',
    observaciones: '备注（可选）',
    timeline: '审批历史',
    formulario: '表单',
    auditLog: '审计日志',
    campoRequerido: '此字段为必填项',
    campoNoSoportado: '此版本不支持该字段',
    sinTipos: '未配置申请类型',
    sinSolicitudes: '您没有申请',
    sinPendientes: '没有待您审批的申请',
    enviadaOk: '申请提交成功',
    borradorGuardado: '草稿已保存',
    errorGenerico: '处理申请时出错',
    configuracion: '配置',
    desdeActividad: '来自流程图活动',
    // Tabs headings / common shared with admin tabs
    aprobadores: '审批人',
    nuevoAprobador: '新增审批人',
    seleccionaTipoMsg: '选择申请类型以查看审批人',
    sinAprobadores: '此类型未配置审批人',
    confirmarEliminarMsg: '确定要删除此项吗？此操作无法撤销。',
    delegaciones: '委派',
    nuevaDelegacion: '新增委派',
    editarDelegacion: '编辑委派',
    sinDelegaciones: '未配置委派',
    escalados: '升级',
    nuevoEscalado: '新增升级',
    editarEscalado: '编辑升级',
    sinEscalados: '未配置升级',
    grupos: '组',
    tipos: '类型',
    todosGrupos: '所有组',
    plantillas: '模板',
    nuevaPlantilla: '新增模板',
    editarPlantilla: '编辑模板',
    sinPlantillas: '未配置模板',
    asuntoPlaceholder: '通知主题',
    tipoSolicitud: '申请类型',
    acciones: '操作',
    opcional: '可选',
    firmada: '已签署',
  },

  // 术语表
  glosario: {
    title: '术语表',
    subtitle: '治理系统的官方术语',
    searchPlaceholder: '搜索术语、定义或同义词...',
    loading: '正在加载术语表...',
    empty: '未找到符合筛选条件的术语',
    errorLoading: '加载术语表时出错',
    all: '全部',
    term: '术语',
    terms: '术语',
    ofTotal: (total: number) => `共 ${total} 条`,
    synonyms: '同义词:',
    noTranslate: '不翻译',
    categoria: {
      INGENIERIA: '工程',
      TIPO_DOCUMENTAL: '文档类型',
      GENERAL: '通用',
      FASE: '阶段',
      ROL: '角色',
      TECNICO: '技术',
    },
    admin: {
      title: '术语表',
      newTerm: '新建术语',
      editTerm: '编辑术语',
      fieldTermino: '术语',
      fieldDefinicion: '定义',
      fieldCategoria: '类别',
      fieldSinonimos: '同义词',
      fieldNoTraducir: '不翻译',
      fieldIdioma: '语言',
      save: '保存',
      cancel: '取消',
      confirmDelete: '从术语表中删除此术语?',
      created: '术语已创建',
      updated: '术语已更新',
      deleted: '术语已删除',
      errorSave: '保存时出错',
      errorDelete: '删除时出错',
      required: '必填字段',
      synonymsHint: '按 Enter 或逗号添加',
      truncated: (n: number) => `${n} 个同义词`,
    },
  },
};
